/*
 * PDP Surgical — replacement picker components.
 *
 * Mounts:
 *   <pdp-surgical-size-picker>  — bucket cards + sub-size pills
 *   <pdp-surgical-frame-picker> — 3 recommended cards + view-all trigger
 *
 * All read state from window.skuPickerInstance.state.confirmed and write
 * back through the existing event surface (size-impact:changed for size,
 * direct mutation + _updateConfirmedSelection() for framing). Pricing,
 * configurator, ATC, gallery image swap — all flow through unchanged.
 *
 * Bucket thresholds match assets/size-impact-slider.js SIZE_BUCKETS:
 *   Nook       max 12 in
 *   Accent     max 16 in
 *   Focal      max 32 in (label "Focal Point")
 *   Statement  max ∞
 */

(() => {
  'use strict';

  // Load translated copy + market-aware shipping data from the JSON island
  // rendered by snippets/pdp-surgical-config.liquid. Liquid is the source of
  // truth for both translation and merchant-configurable values (shipping
  // threshold lives on localization.market.metafields.custom).
  const I18N = (() => {
    try {
      const node = document.querySelector('script[data-pdp-surgical-i18n]');
      if (!node) return {};
      return JSON.parse(node.textContent || '{}');
    } catch (e) {
      console.warn('[pdp-surgical] i18n island missing or invalid', e);
      return {};
    }
  })();
  const T = (path, fallback) => {
    const v = path.split('.').reduce((o, k) => (o == null ? o : o[k]), I18N);
    return (v == null || v === '') ? (fallback ?? '') : v;
  };

  const SIZE_BUCKETS = [
    { id: 'XS', name: T('size.buckets.nook.name',      'Nook'),      max: 12,       desc: T('size.buckets.nook.desc',      'Petite — clusters and gallery walls.') },
    { id: 'S',  name: T('size.buckets.accent.name',    'Accent'),    max: 16,       desc: T('size.buckets.accent.desc',    'Versatile and balanced. Ideal for entryways and desks.') },
    { id: 'M',  name: T('size.buckets.focal.name',     'Focal'),     max: 32,       desc: T('size.buckets.focal.desc',     'A confident centerpiece for living rooms and beds.'), popular: true },
    { id: 'L',  name: T('size.buckets.statement.name', 'Statement'), max: Infinity, desc: T('size.buckets.statement.desc', 'Floor-to-ceiling drama for big walls.') },
  ];

  const FRAMING_DISPLAY_NAMES = {
    'unframed': T('frame.format_unframed', 'Print only'),
    'classic-frame': 'Classic',
    'metallic-classic-frame': 'Metallic',
    'box-frame': 'Box frame',
  };

  // Re-emit the legacy style-picker:preset-selected DOM event so
  // posthog-events.js keeps capturing `style_preset_selected`. The surgical
  // pickers replace style-picker.js's own UI (which dispatched this event),
  // so without this the framing analytics go dark on the surgical PDP.
  // Fire only on deliberate user selection — never on programmatic fallbacks.
  function emitPresetSelected(detail) {
    document.dispatchEvent(new CustomEvent('style-picker:preset-selected', {
      detail: {
        presetId:   detail.presetId   ?? null,
        presetName: detail.presetName ?? null,
        framing:    detail.framing    ?? null,
        frameColor: detail.frameColor ?? null,
        border:     detail.border     ?? null,
        source:     detail.source     ?? null,
      },
    }));
  }

  // ============================================================
  // Wait for skuPickerInstance — it boots async after DOMContentLoaded
  // ============================================================
  function whenSkuPickerReady(cb) {
    if (window.skuPickerInstance && window.skuPickerInstance.data) {
      cb(window.skuPickerInstance);
      return;
    }
    let tries = 0;
    const t = setInterval(() => {
      tries++;
      if (window.skuPickerInstance && window.skuPickerInstance.data) {
        clearInterval(t);
        cb(window.skuPickerInstance);
      } else if (tries > 80) { // 8s
        clearInterval(t);
        console.warn('[pdp-surgical] skuPickerInstance never appeared');
      }
    }, 100);
  }

  function getCurrencyFmt() {
    return (cents) => {
      const sp = window.skuPickerInstance;
      if (sp && typeof sp._formatPrice === 'function') return sp._formatPrice(cents);
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
          style: 'currency',
          currency: window.Shopify?.currency?.active || 'GBP',
          minimumFractionDigits: 2,
        }).format(cents / 100);
      } catch {
        return `£${(cents / 100).toFixed(2)}`;
      }
    };
  }

  // ── Hybrid configurator helpers ─────────────────────────────────────────
  function getHybridCfg() {
    try {
      const raw = document.querySelector('script[data-pdp-surgical-i18n]')?.textContent || '{}';
      const h = JSON.parse(raw).hybrid;
      if (!h) return null;
      const clean = (arr) => (Array.isArray(arr) ? arr.map(s => String(s).trim()).filter(Boolean) : []);
      return {
        enabled: !!h.enabled,
        sets: { portrait: clean(h.sets?.portrait), square: clean(h.sets?.square), landscape: clean(h.sets?.landscape) },
        popular: (h.popular || '').trim(),
        suggested: (h.suggested_handle || '').trim(),
      };
    } catch { return null; }
  }
  // Module-level orientation helper — single implementation shared by both
  // SurgicalSizePicker._applyHybrid() and SurgicalFramePicker._artworkOrientation()
  // (which delegates here). Keeps orientation logic consistent across pickers.
  function artworkOrientation() {
    let aspect = parseFloat(document.querySelector('style-picker')?.dataset.artworkAspect || '');
    if (!aspect || !isFinite(aspect)) {
      const img = document.querySelector('[data-slide-role="frame-mock"] .mock-image__placement img')
        || document.querySelector('media-gallery .mock-image__placement img');
      if (img?.naturalWidth && img?.naturalHeight) aspect = img.naturalWidth / img.naturalHeight;
    }
    if (!aspect || !isFinite(aspect)) return 'portrait';
    return aspect < 0.95 ? 'portrait' : aspect > 1.05 ? 'landscape' : 'square';
  }
  let _sheetEl = null, _sheetReturnFocus = null, _sheetRoomReadyHandler = null;
  function _sheetWriteUnit(u) {
    try { localStorage.setItem('pdp_unit', u); localStorage.setItem('skuPicker_unitPreference', u); } catch {}
  }
  function closeSizeSheet() {
    if (!_sheetEl) return;
    try { _sheetEl.close(); } catch {}
    if (_sheetRoomReadyHandler) {
      document.removeEventListener('room-background:ready', _sheetRoomReadyHandler);
      _sheetRoomReadyHandler = null;
    }
    document.documentElement.style.overflow = '';
    _sheetEl.remove(); _sheetEl = null;
    _sheetReturnFocus?.focus?.();
  }
  function openSizeSheet(sp, sizes) {
    if (_sheetEl) return;
    const fmt = getCurrencyFmt();
    const SOFA_CM = 160; // 2-seat sofa reference width
    // The PDP's own picker is the unit source of truth (locale-aware default)
    // — and the sheet writes back through it so a toggle here persists on the
    // PDP after close (QA 09/07, Luke).
    const livePicker = document.querySelector('.product-information--surgical pdp-size-picker')
      || document.querySelector('pdp-size-picker');
    let unit = livePicker?.unit
      || (() => { try { return localStorage.getItem('pdp_unit') === 'in' ? 'in' : 'cm'; } catch { return 'cm'; } })();
    let sel = sp.state?.confirmed?.size_option || sizes[0]?.handle || null;
    const opt = (h) => sizes.find(s => s.handle === h);
    const dimOf = (s) => unit === 'cm'
      ? `${Math.round(parseFloat(s.width_cms))} × ${Math.round(parseFloat(s.height_cms))} cm`
      : `${(+parseFloat(s.width_inches)).toString()} × ${(+parseFloat(s.height_inches)).toString()}″`;

    _sheetReturnFocus = document.activeElement;
    const dlg = document.createElement('dialog');
    dlg.className = 'pdp-size-sheet';
    dlg.innerHTML = `
      <div class="pss__head">
        <h2>${T('size.sheet_title', 'Choose your size')}</h2>
        <span class="sp"></span>
        <span class="pss__unit" role="group" aria-label="Unit">
          <button type="button" data-unit="in">in</button><button type="button" data-unit="cm">cm</button>
        </span>
        <button type="button" class="pss__x" data-close aria-label="Close">✕</button>
      </div>
      <div class="pss__room">
        <div class="pss__sofa"></div><div class="pss__frame" data-room-frame></div>
        <div class="pss__mock" data-room-mock hidden></div>
      </div>
      <div class="pss__cap" data-room-cap></div>
      <div class="pss__grid" data-grid></div>
      <div class="pss__foot"><button type="button" class="pss__apply" data-apply></button></div>`;
    document.body.appendChild(dlg);
    _sheetEl = dlg;
    document.documentElement.style.overflow = 'hidden';
    dlg.showModal(); // native focus-trap + Esc

    // Analytics hook (posthog-events.js → size_sheet_opened). The "View all
    // N sizes" sheet had no open signal, so modal engagement was invisible.
    document.dispatchEvent(new CustomEvent('pdp:size-sheet-opened', {
      detail: { selected: sel },
    }));

    const grid = dlg.querySelector('[data-grid]');
    const frameEl = dlg.querySelector('[data-room-frame]');
    const capEl = dlg.querySelector('[data-room-cap]');
    const applyEl = dlg.querySelector('[data-apply]');
    const mockLayer = dlg.querySelector('[data-room-mock]');
    const sofaEl = dlg.querySelector('.pss__sofa');

    // Real room mock per highlighted size — a CLONE of the gallery's position-2
    // <room-background-mock> (the "view in room" slide), driven through the same
    // handleConfigChange({ wallDimensions }) API the sku picker uses, so the
    // artwork rescales true-to-size against the room photo. Calling the method
    // on the clone (instead of dispatching gallery-wall:config-changed) keeps
    // the sheet's highlight from mutating the real gallery slide behind it.
    let roomMock = null;
    let roomMockReady = false;
    let pendingRoomDims = null;
    function applyRoomDims(dims) {
      // The clone initializes async (room fetch + parseData) — calling before
      // ready throws on unparsed internal state, so buffer until its
      // room-background:ready fires.
      if (!roomMockReady) { pendingRoomDims = dims; return; }
      try {
        roomMock.handleConfigChange({ detail: dims });
      } catch { /* keep the last good render */ }
    }
    function ensureRoomMock() {
      if (roomMock) return roomMock;
      const src = document.querySelector('.product-information--surgical media-gallery room-background-mock');
      if (!src || typeof src.handleConfigChange !== 'function') return null;
      roomMock = src.cloneNode(false); // fresh element — connectedCallback builds its own DOM
      roomMock.removeAttribute('id');
      // Handler kept module-level so closeSizeSheet() can remove it — one was
      // leaked per open/close cycle before (PR #165 review).
      _sheetRoomReadyHandler = (e) => {
        if (e.detail?.source !== roomMock) return;
        roomMockReady = true;
        if (pendingRoomDims) { const d = pendingRoomDims; pendingRoomDims = null; applyRoomDims(d); }
      };
      document.addEventListener('room-background:ready', _sheetRoomReadyHandler);
      mockLayer.appendChild(roomMock);
      return roomMock;
    }

    function renderRoom() {
      const s = opt(sel); if (!s) return;
      const name = s.displayName || s.name || dimOf(s);
      const rm = ensureRoomMock();
      if (rm) {
        mockLayer.hidden = false;
        frameEl.style.display = 'none';
        sofaEl.style.display = 'none';
        // The room box is a SQUARE that hugs the square render (CSS
        // aspect-ratio: 1) so the FULL image shows — no crop (QA 09/07,
        // Luke; supersedes the earlier cover-crop). Math.min keeps contain
        // semantics even if the box ever isn't square.
        mockLayer.parentElement.classList.add('pss__room--mock');
        const side = Math.min(mockLayer.clientWidth || Infinity, mockLayer.clientHeight || Infinity);
        const sidePx = isFinite(side) && side > 0 ? side : 300;
        rm.style.height = sidePx + 'px';
        rm.style.width = sidePx + 'px';
        const wi = parseFloat(s.width_inches) || (parseFloat(s.width_cms) || 30) / 2.54;
        const hi = parseFloat(s.height_inches) || (parseFloat(s.height_cms) || 40) / 2.54;
        applyRoomDims({ size: s.handle, wallDimensions: { widthInches: wi, heightInches: hi }, artworkWidth: wi });
        capEl.textContent = (T('size.room_caption_mock', '{{ name }} ({{ dim }}) shown in a room setting'))
          .replace('{{ name }}', name).replace('{{ dim }}', dimOf(s));
        return;
      }
      mockLayer.hidden = true;
      mockLayer.parentElement.classList.remove('pss__room--mock');
      frameEl.style.display = '';
      sofaEl.style.display = '';
      const wc = parseFloat(s.width_cms) || 30, hc = parseFloat(s.height_cms) || 40;
      // Size the frame in px from the real dimensions against the sofa reference so
      // the aspect ratio is always exact; clamp to the room box (preserving aspect).
      const box = frameEl.parentElement.getBoundingClientRect();
      const boxW = box.width || 400, boxH = box.height || 150;
      const pxPerCm = (boxW * 0.62) / SOFA_CM; // sofa ≈ 62% of room width = SOFA_CM
      let w = wc * pxPerCm, h = hc * pxPerCm;
      const k = Math.min(1, (boxW * 0.82) / w, (boxH - 64) / h); // fit within box, above the sofa
      w *= k; h *= k;
      frameEl.style.width = Math.round(w) + 'px';
      frameEl.style.height = Math.round(h) + 'px';
      frameEl.style.aspectRatio = '';
      capEl.textContent = (T('size.room_caption', '{{ name }} ({{ dim }}) shown above a standard 2-seat sofa'))
        .replace('{{ name }}', name).replace('{{ dim }}', dimOf(s));
    }
    function renderApply() {
      const s = opt(sel); if (!s) return;
      applyEl.textContent = (T('size.sheet_apply', 'Choose {{ name }} · {{ price }}'))
        .replace('{{ name }}', s.displayName || s.name || dimOf(s)).replace('{{ price }}', s.price != null ? fmt(s.price) : '');
    }
    function renderGrid() {
      grid.innerHTML = sizes.map(s => {
        const on = s.handle === sel;
        const disabled = s.blocked || s.soldOut;
        return `<button type="button" class="pss-card ${on ? 'on' : ''} ${disabled ? 'blocked' : ''}" data-handle="${s.handle}" ${disabled ? 'disabled' : ''}>
          <div class="n">${s.displayName || s.name || dimOf(s)}</div><div class="d">${dimOf(s)}</div>
          <div class="pr">${s.price != null ? fmt(s.price) : ''}</div></button>`;
      }).join('');
      grid.querySelectorAll('.pss-card[data-handle]').forEach(c => c.addEventListener('click', () => {
        if (c.disabled) return; sel = c.dataset.handle; renderGrid(); renderRoom(); renderApply();
      }));
    }
    function setUnit(u) {
      if (u === unit) return; unit = u; _sheetWriteUnit(u);
      if (livePicker) livePicker.unit = u; // sync the PDP picker's in/cm toggle live
      dlg.querySelectorAll('[data-unit]').forEach(b => b.classList.toggle('on', b.dataset.unit === u));
      renderGrid(); renderRoom(); renderApply();
    }
    dlg.querySelectorAll('[data-unit]').forEach(b => { b.classList.toggle('on', b.dataset.unit === unit); b.addEventListener('click', () => setUnit(b.dataset.unit)); });
    dlg.querySelectorAll('[data-close]').forEach(b => b.addEventListener('click', closeSizeSheet));
    dlg.addEventListener('cancel', (e) => { e.preventDefault(); closeSizeSheet(); }); // Esc
    dlg.addEventListener('click', (e) => { if (e.target === dlg) closeSizeSheet(); }); // backdrop
    applyEl.addEventListener('click', () => {
      const container = sp.container || document;
      container.dispatchEvent(new CustomEvent('size-impact:changed', { bubbles: true, detail: { handle: sel } }));
      // Analytics hook (posthog-events.js → size_sheet_applied): a size chosen
      // FROM the sheet, distinct from inline picker taps (size_changed).
      document.dispatchEvent(new CustomEvent('pdp:size-sheet-applied', {
        detail: { handle: sel },
      }));
      closeSizeSheet();
    });
    renderGrid(); renderRoom(); renderApply();
  }

  function classifyBucket(size) {
    // Match assets/size-impact-slider.js: classify by max dimension.
    // Most art is portrait so height is usually the longer edge.
    const w = parseFloat(size?.width_inches) || 0;
    const h = parseFloat(size?.height_inches) || 0;
    const max = Math.max(w, h);
    for (const b of SIZE_BUCKETS) {
      if (max <= b.max) return b;
    }
    return SIZE_BUCKETS[SIZE_BUCKETS.length - 1];
  }

  // ============================================================
  // REFERENCE PRICING — everything is anchored to the unframed print.
  //
  // Size cards always show the unframed print price for that size, and
  // style cards show the delta between the unframed print and that style's
  // framed total — even when the style lives on another Shopify variant
  // (e.g. canvas, whose base price includes the wrapped stretcher). Without
  // a fixed anchor the size cards re-price whenever a style selection
  // switches sp.currentVariantId, which reads as the sizes changing price.
  // Invariant: selected size price + selected style delta = displayed total.
  // ============================================================

  /** The art-print variant's data, or the only/first variant on
   *  single-medium products. */
  function printVariantOf(sp) {
    const variants = Object.values(sp?.variantConfigurators?.variants || {});
    return variants.find(v => v.artProduct !== 'canvas-print') || variants[0] || null;
  }

  /** Base (unframed, no-border) price of a size on one variant, in cents. */
  function baseSizePrice(variantData, sizeHandle) {
    if (!variantData || !sizeHandle) return null;
    const pricing = variantData.size_pricing;
    if (!Array.isArray(pricing)) return null;
    const base = pricing.find(e => e.size_option === sizeHandle && !e.framing_option && !e.border_option);
    if (base?.price != null) return base.price;
    const any = pricing.find(e => e.size_option === sizeHandle);
    return any?.price ?? null;
  }

  /** The unframed print price for a size — the reference all size-card
   *  prices and style-card deltas are expressed against. */
  function printBasePriceFor(sp, sizeHandle) {
    return baseSizePrice(printVariantOf(sp), sizeHandle);
  }

  /** Delta between the unframed print and a style's framed total:
   *  (style variant base + framing add + border add) − print base.
   *  `framing`/`border` are resolved against the style's own variant.
   *  Returns null when the combination isn't priced for this size. */
  function styleDeltaFor(sp, sizeHandle, { variantId, framing, border }) {
    const vd = sp?.variantConfigurators?.variants?.[variantId] || null;
    const styleBase = baseSizePrice(vd, sizeHandle);
    const printBase = printBasePriceFor(sp, sizeHandle);
    if (styleBase == null || printBase == null) return null;
    let add = 0;
    if (framing && framing !== 'unframed') {
      const f = vd.framing_pricing?.find(e => e.size_option === sizeHandle && e.framing_option === framing);
      if (!f) return null;
      add += f.price || 0;
    }
    if (border && border !== 'no-border') {
      const b = vd.border_pricing?.find(e => e.size_option === sizeHandle && e.border_option === border);
      add += b?.price || 0;
    }
    return styleBase + add - printBase;
  }

  // Exposed for pdp-configurator-v2.js so its price slots use the SAME anchor
  // as the style cards (unframed print + full style delta).
  window.__pdpRefPricing = { printBasePriceFor, styleDeltaFor };

  // ============================================================
  // SIZE PICKER — adapter around the shared <pdp-size-picker> element.
  //
  // Mounts a generic <pdp-size-picker> child and feeds it from
  // skuPickerInstance. All visual rendering lives in the shared
  // component (assets/pdp-size-picker.{js,css}); this class just
  // bridges the data and the change events.
  // ============================================================
  class SurgicalSizePicker extends HTMLElement {
    constructor() { super(); this._unsubs = []; }

    connectedCallback() {
      whenSkuPickerReady((sp) => this._init(sp));
    }
    disconnectedCallback() { this._unsubs.forEach(fn => fn()); this._unsubs = []; }

    _init(sp) {
      // Move-safety: the vibe quick-view adopts the surgical root into
      // #sku-picker-container (and sku-picker's initial render can evict and
      // re-adopt it), so this element connects more than once. The DOM build
      // below must run once; only the document-level subscriptions (torn down
      // by disconnectedCallback) are re-established per connection.
      if (!this.isConnected) return;      // adopted away while the ready-poll was pending
      if (this._unsubs.length) return;    // already live for this connection
      this.sp = sp;
      if (this._picker) { this._subscribe(); return; }

      // Mount the shared picker as a child element.
      this._picker = document.createElement('pdp-size-picker');
      this._picker.classList.add('pdp-surgical-size-picker');
      this._picker.setAttribute('advisor-intent', 'size');
      if (this.hasAttribute('default-unit')) this._picker.setAttribute('default-unit', this.getAttribute('default-unit'));
      this._picker.headerLabel = T('size.label', 'Size');
      this._picker.sizeGuideLabel = T('size.guide_link', 'Size Guide');
      // Bucket vocabulary from the i18n island — the component's built-in
      // names are English fallbacks only (see pdp-size-picker.js `buckets`).
      this._picker.buckets = [
        { id: 'XS', name: T('size.buckets.nook.name', 'Nook'),      desc: T('size.buckets.nook.desc', '') },
        { id: 'S',  name: T('size.buckets.accent.name', 'Accent'),  desc: T('size.buckets.accent.desc', '') },
        { id: 'M',  name: T('size.buckets.focal.name', 'Focal'),    desc: T('size.buckets.focal.desc', '') },
        { id: 'L',  name: T('size.buckets.statement.name', 'Statement'), desc: T('size.buckets.statement.desc', '') },
      ];
      this._picker.popularLabel = T('size.popular_badge', 'Popular');
      this._picker.advisorPrompt = T('size.advisor_prompt', 'Not sure?');
      this._picker.advisorLinkLabel = T('size.advisor_link', 'Ask the Art Advisor for the right size');
      this._picker.formatPrice = getCurrencyFmt();
      this.appendChild(this._picker);

      // User picked a size → dispatch the existing size-impact:changed
      // event so sku-picker handles state + variant + image swap.
      this._picker.addEventListener('pdp-size-picker:change', (e) => {
        const handle = e.detail?.handle;
        if (!handle) return;
        const container = this.sp.container || document;
        container.dispatchEvent(new CustomEvent('size-impact:changed', {
          bubbles: true,
          detail: { handle },
        }));
        // Optimistically reflect the choice; sku-picker's selection-changed
        // event (below) will push the authoritative value back next tick.
        this._picker.value = handle;
      });
      // Unit toggle → broadcast for any other listeners.
      this._picker.addEventListener('pdp-size-picker:unit-change', (e) => {
        document.dispatchEvent(new CustomEvent('sku-picker:unit-changed', {
          bubbles: true,
          detail: { unit: e.detail?.unit },
        }));
      });

      this._subscribe();
    }

    // Document-level listeners — re-established on every (re)connection,
    // removed by disconnectedCallback via _unsubs.
    _subscribe() {
      this._sync();
      const onSel = () => this._sync();
      document.addEventListener('sku-picker:selection-changed', onSel);
      document.addEventListener('sku-picker:variant-changed', onSel);
      document.addEventListener('sku-picker:unit-changed', onSel);
      this._unsubs.push(() => document.removeEventListener('sku-picker:selection-changed', onSel));
      this._unsubs.push(() => document.removeEventListener('sku-picker:variant-changed', onSel));
      this._unsubs.push(() => document.removeEventListener('sku-picker:unit-changed', onSel));
    }

    _getOptions() {
      // Use the union of sizes across ALL variants — same as size-impact-slider via
      // sku-picker._getUnionSizeOptions(). sp.data.options.size is variant-specific
      // (set to whichever variant ?variant= resolved to), so reading it alone would
      // hide sizes that belong to the other variant (e.g. canvas vs art print).
      return this.sp._getUnionSizeOptions?.() || this.sp.data?.options?.size || [];
    }

    _getCurrentHandle() {
      return this.sp.state?.confirmed?.size_option || null;
    }

    // Friendly size label by max dimension (matches pdp-size-picker.js SIZE_BUCKETS:
    // Nook ≤12in, Accent ≤16in, Focal ≤32in, else Statement) so the sheet shows a
    // name like "Accent" beside the dimension, parity with the inline picker.
    _bucketName(opt) {
      const wi = parseFloat(opt.width_inches) || 0, hi = parseFloat(opt.height_inches) || 0;
      let mx = Math.max(wi, hi);
      if (!mx) mx = Math.max(parseFloat(opt.width_cms) || 0, parseFloat(opt.height_cms) || 0) / 2.54;
      if (mx <= 12) return T('size.buckets.nook.name', 'Nook');
      if (mx <= 16) return T('size.buckets.accent.name', 'Accent');
      if (mx <= 32) return T('size.buckets.focal.name', 'Focal');
      return T('size.buckets.statement.name', 'Statement');
    }

    _priceFor(handle) {
      const sp = this.sp;
      // Always the unframed PRINT price — never the current variant's base.
      // Style selection may switch sp.currentVariantId to the canvas variant,
      // and pricing sizes from it would make every size card jump to the
      // wrapped-canvas price. The style card carries that delta instead.
      const printBase = printBasePriceFor(sp, handle);
      if (printBase != null) return printBase;
      // Size not offered as a print — fall back to any variant that prices it.
      for (const variant of Object.values(sp.variantConfigurators?.variants || {})) {
        const price = baseSizePrice(variant, handle);
        if (price != null) return price;
      }
      const comp = sp.components?.size?.[handle];
      if (comp?.price) return comp.price;
      return null;
    }

    _sync() {
      if (!this._picker) return;
      const sizes = this._getOptions();
      this._picker.options = sizes.map(s => ({
        handle:        s.handle,
        name:          s.name,
        width_inches:  s.width_inches,
        height_inches: s.height_inches,
        width_cms:     s.width_cms,
        height_cms:    s.height_cms,
        price:         this._priceFor(s.handle),
      }));
      const cur = this._getCurrentHandle();
      this._picker.value = cur || sizes[0]?.handle || null;
      this._applyHybrid();
    }

    _applyHybrid() {
      const cfg = getHybridCfg();
      if (!cfg || !cfg.enabled || !this._picker) return false;
      const orient = artworkOrientation();
      const set = cfg.sets[orient] || [];
      if (!set.length) return false;
      const sizes = this._getOptions();
      const avail = new Map(sizes.filter(s => !s.blocked && !s.soldOut).map(s => [s.handle, s]));
      const maxDim = (s) => Math.max(parseFloat(s.width_inches) || 0, parseFloat(s.height_inches) || 0)
                          || Math.max(parseFloat(s.width_cms) || 0, parseFloat(s.height_cms) || 0) / 2.54;
      const resolved = set.filter(h => avail.has(h));
      if (resolved.length < set.length) {
        const extra = [...avail.values()].filter(s => !resolved.includes(s.handle)).sort((a, b) => maxDim(a) - maxDim(b)).map(s => s.handle);
        for (const h of extra) { if (resolved.length >= set.length) break; resolved.push(h); }
      }
      if (!resolved.length) return false;
      const rec = (cfg.suggested && avail.has(cfg.suggested)) ? cfg.suggested
                : (cfg.popular && avail.has(cfg.popular)) ? cfg.popular : resolved[0];
      // Badge is decoupled from `rec` (which also seeds the initial selection —
      // the GB/EU lowest-price default must stay). Only badge a size that is
      // visible in the flat row, and never the smallest card: the configurator
      // default IS the smallest on lowest-price-default markets, and
      // "Suggested: <smallest>" reads as noise (QA thread).
      let badge = null;
      if (cfg.suggested && resolved.includes(cfg.suggested) && cfg.suggested !== resolved[0]) {
        badge = cfg.suggested;
      } else if (cfg.popular && resolved.includes(cfg.popular) && cfg.popular !== resolved[0]) {
        badge = cfg.popular;
      }
      const recLabel = (badge === cfg.popular) ? T('hybrid.most_popular', 'Most popular') : T('hybrid.suggested', 'Suggested');
      this._picker.flat = true;
      this._picker.curatedHandles = resolved;
      this._picker.recommendedHandle = badge;
      this._picker.recommendedLabel = badge ? recLabel : '';
      this._picker.fromLabel = T('size.from', 'from');
      this._picker.seeAllLabel = `${T('size.view_all_lead', 'View all')} <b>${sizes.length} ${T('size.view_all_sizes', 'sizes')}</b>`;
      if (!this._getCurrentHandle() && rec) {
        const container = this.sp.container || document;
        container.dispatchEvent(new CustomEvent('size-impact:changed', { bubbles: true, detail: { handle: rec } }));
        this._picker.value = rec;
      }
      if (!this._seeAllWired) {
        this._seeAllWired = true;
        this._picker.addEventListener('pdp-size-picker:see-all', () => {
          const pricedOptions = this._getOptions().map(s => ({ ...s, price: this._priceFor(s.handle), displayName: this._bucketName(s) }));
          openSizeSheet(this.sp, pricedOptions);
        });
      }
      return true;
    }
  }
  customElements.define('pdp-surgical-size-picker', SurgicalSizePicker);

  // ============================================================
  // FRAME PICKER — surgical card presentation, style-picker logic
  //
  // Preset resolution + availability filtering lives entirely in
  // style-picker.js. SurgicalFramePicker reads the already-resolved
  // presets via stylePicker.resolvedPresets, filters by current-size
  // availability using the same pricing check as #isPresetAvailable,
  // then renders its own surgical card layout.
  // ============================================================
  class SurgicalFramePicker extends HTMLElement {
    constructor() {
      super();
      this._unsubs = [];
      this._sp = null;
      this._renderedPresetKeys = [];  // stable keys of last-rendered preset set
      this._mockCache = new Map();    // presetKey → detached gallery-wall-frame-mock node
    }

    connectedCallback() { whenSkuPickerReady(sp => this._init(sp)); }
    disconnectedCallback() { this._unsubs.forEach(fn => fn()); this._unsubs = []; }

    _init(sp) {
      // Move-safety (same rationale as SurgicalSizePicker._init): re-runs on
      // every reconnection — _render() rebuilds idempotently, and all
      // subscriptions below are registered against _unsubs, which
      // disconnectedCallback cleared — so guard only against double-init
      // within a single connection and init-after-eviction.
      if (!this.isConnected) return;
      if (this._unsubs.length) return;
      this._sp = sp;

      const doRender = () => this._render();
      const doSelChange = () => this._onSelectionChanged();
      const doSizeChange = () => this._onSizeChanged();

      const waitForStylePicker = () => {
        const el = document.querySelector('style-picker');
        if (el?.resolvedPresets?.length) {
          this._stylePicker = el;
          this._render();
        } else {
          const onReady = () => { this._stylePicker = document.querySelector('style-picker'); this._render(); };
          document.addEventListener('style-picker:ready', onReady, { once: true });
          this._unsubs.push(() => document.removeEventListener('style-picker:ready', onReady));
        }
      };

      waitForStylePicker();

      document.addEventListener('sku-picker:variant-changed', doRender);
      document.addEventListener('sku-picker:selection-changed', doSelChange);
      document.addEventListener('size-impact:changed', doSizeChange);
      this._unsubs.push(
        () => document.removeEventListener('sku-picker:variant-changed', doRender),
        () => document.removeEventListener('sku-picker:selection-changed', doSelChange),
        () => document.removeEventListener('size-impact:changed', doSizeChange),
      );
    }

    // ── Preset data from style-picker ─────────────────────────────────────

    /** Full list of style-picker presets available for the current size, unsliced.
     *  Mirrors style-picker.js #isPresetAvailable — uses resolved handles, no string matching. */
    _getAllAvailablePresets() {
      const sp = this._sp;
      const allPresets = this._stylePicker?.resolvedPresets || [];
      const currentSize = sp.state?.confirmed?.size_option;
      const currentVid = sp.currentVariantId;

      return allPresets.filter(p => {
        if (p.framing === 'unframed') return false;
        if (!currentSize) return true;

        if (p.resolvedVariantId === currentVid) {
          if (!sp.components?.framing?.[currentSize]?.[p.resolvedFraming]) return false;
          if (p.border === 'mount' && !sp.components?.border?.[currentSize]?.['mount']) return false;
        } else {
          const vd = sp.variantConfigurators?.variants?.[p.resolvedVariantId];
          if (!vd) return false;
          const hasFraming = vd.framing_pricing?.some(
            e => e.size_option === currentSize && e.framing_option === p.resolvedFraming
          );
          if (!hasFraming) return false;
          if (p.border === 'mount') {
            const hasBorder = vd.border_pricing?.some(
              e => e.size_option === currentSize && e.border_option === 'mount'
            );
            if (!hasBorder) return false;
          }
        }

        // Final guard: a purchasable Prodigi SKU must exist for this combination
        const artProduct = sp.variantConfigurators?.variants?.[p.resolvedVariantId]?.artProduct || null;
        const prodigiConfig = sp._lookupProdigiConfig?.(currentSize, p.resolvedFraming, p.border, artProduct);
        if (prodigiConfig && !prodigiConfig.prodigi_sku) return false;

        return true;
      });
    }

    /** Recommended trio — first 3 of the available presets. */
    _getAvailablePresets() {
      return this._getAllAvailablePresets().slice(0, 3);
    }

    /** Distinct frame-colour swatch sources from the full available-preset set,
     *  in render order. Each entry is either { tile: <url> } (preferred — a
     *  tileable image of the frame finish) or { hex: <#hex> } (fallback when
     *  color_value is set). De-duplicated by colour handle so we don't show
     *  the same finish twice across two framings. Used by the "View all N
     *  frames" CTA. `excludeHandles` skips colours already visible on the
     *  recommended cards above the pill — design QA 09/07 (Vince): show only
     *  the colours NOT on display, so the pill copy fits one line. */
    _getAvailableSwatchSources(limit = 6, excludeHandles = null) {
      const sp = this._sp;
      const seenHandles = new Set(excludeHandles || []);
      const out = [];
      for (const p of this._getAllAvailablePresets()) {
        if (!p.frameColorHandle || seenHandles.has(p.frameColorHandle)) continue;
        const vd = sp.variantConfigurators?.variants?.[p.resolvedVariantId];
        const framing = vd?.options?.framing?.find(f => f.handle === p.resolvedFraming);
        const colorObj = framing?.colors?.find(c => c.handle === p.frameColorHandle);
        if (!colorObj) continue;
        seenHandles.add(p.frameColorHandle);
        if (colorObj.moulding_tile) {
          out.push({ tile: colorObj.moulding_tile });
        } else if (colorObj.color_value) {
          out.push({ hex: colorObj.color_value });
        } else {
          continue;
        }
        if (out.length >= limit) break;
      }
      return out;
    }

    /** Card delta for a preset: framed total − unframed print (see the
     *  REFERENCE PRICING block). Includes the base-price difference when the
     *  preset lives on another variant (canvas) and any border (mount) add. */
    _getFrameAdd(preset) {
      const sp = this._sp;
      const sz = sp.state?.confirmed?.size_option;
      if (!sz) return 0;
      return styleDeltaFor(sp, sz, {
        variantId: preset.resolvedVariantId || sp.currentVariantId,
        framing: preset.resolvedFraming,
        border: preset.border || 'no-border',
      }) ?? 0;
    }

    /** Delta for the unframed card in the CURRENT medium: 0 for "Print
     *  only", canvas base − print base for "Wrapped Canvas". */
    _getUnframedAdd() {
      const sp = this._sp;
      const sz = sp.state?.confirmed?.size_option;
      if (!sz) return 0;
      return styleDeltaFor(sp, sz, {
        variantId: sp.currentVariantId,
        framing: 'unframed',
        border: 'no-border',
      }) ?? 0;
    }

    _productTypeFor(variantId) {
      const vd = this._sp.variantConfigurators?.variants?.[variantId];
      return vd?.artProduct === 'canvas-print' ? 'canvas' : 'art_print';
    }

    _presetKey(p) {
      return p ? `${p.resolvedVariantId}|${p.resolvedFraming}|${p.frameColorHandle}|${p.border || 'no-border'}` : 'unframed';
    }

    // ── Custom frame detection ─────────────────────────────────────────────

    /** True when the current framing+color+border doesn't match any preset.
     *  Border is part of the identity — box-frame+white+mount (a named preset)
     *  and box-frame+white+no-border (custom) must not be conflated. */
    _isCustomActive() {
      const sp = this._sp;
      const curFraming = sp.state?.confirmed?.framing_option;
      const curColor = sp.state?.confirmed?.frame_color;
      const curBorder = sp.state?.confirmed?.border_option || 'no-border';
      if (!curFraming || curFraming === 'unframed') return false;
      // Deliberately checks the visible top-3 row, not all presets: a
      // selection matching a preset that isn't rendered must still surface
      // as the position-0 card (where _getCustomItem recovers the exact
      // preset's name/mock), otherwise no card in the row shows as active.
      return !this._getAvailablePresets().some(
        p => p.resolvedFraming === curFraming
          && p.frameColorHandle === curColor
          && (p.border || 'no-border') === curBorder
      );
    }

    /** Check if a given framing handle is purchasable for the given size. */
    _isFramingAvailableForSize(framingHandle, size) {
      const sp = this._sp;
      if (!framingHandle || framingHandle === 'unframed' || !size) return true;
      if (sp.components?.framing?.[size]?.[framingHandle]) return true;
      return Object.values(sp.variantConfigurators?.variants || {}).some(vd =>
        vd.framing_pricing?.some(e => e.size_option === size && e.framing_option === framingHandle)
      );
    }

    /** Display name for a framing+colour+border selection. An exact preset
     *  match returns the preset name (which already carries the medium, e.g.
     *  "The Bold Canvas"). A custom config returns a synthetic
     *  "Framing · Colour" label, with " Canvas" appended for canvas products
     *  so a custom canvas frame never reads the same as the print version. */
    _frameDisplayName(framing, color, border, variantId) {
      const sp = this._sp;
      const presets = this._stylePicker?.resolvedPresets || [];
      const matched = presets.find(
        p => p.resolvedFraming === framing
          && p.frameColorHandle === color
          && (p.border || 'no-border') === (border || 'no-border')
      );
      if (matched) return matched.name;
      const variant = sp.variantConfigurators?.variants?.[variantId];
      const allFraming = variant?.options?.framing || sp.data?.options?.framing || [];
      const opt = allFraming.find(o => o.handle === framing);
      const col = (opt?.colors || []).find(c => c.handle === color);
      let label = (opt && col) ? `${opt.short_name} · ${col.short_name}` : (opt?.short_name || 'Custom');
      // Append a non-default border (e.g. a mount) so the label reflects the
      // full custom configuration.
      if (border && border !== 'no-border') {
        const borderOpt = (variant?.options?.border || sp.data?.options?.border || [])
          .find(b => b.handle === border);
        const borderLabel = borderOpt && (borderOpt.label || borderOpt.name || borderOpt.short_name);
        if (borderLabel) label = `${label} · ${borderLabel}`;
      }
      if (variant?.artProduct === 'canvas-print') {
        label = `${label} Canvas`;
      }
      return label;
    }

    /** Build item data for the custom card at position 0. */
    _getCustomItem() {
      const sp = this._sp;
      const curFraming = sp.state?.confirmed?.framing_option;
      const curColor = sp.state?.confirmed?.frame_color;
      const border = sp.state?.confirmed?.border_option || 'no-border';

      // Prefer a matching named preset for correct mock/name data — but only
      // an EXACT match including border, so a config that differs only in
      // border (e.g. no mount) renders as a true Custom card, not the preset.
      const allPresets = this._stylePicker?.resolvedPresets || [];
      const matched = allPresets.find(
        p => p.resolvedFraming === curFraming
          && p.frameColorHandle === curColor
          && (p.border || 'no-border') === border
      );
      if (matched) return { isCustom: true, preset: matched, isUnframed: false };

      // Build a synthetic preset-like from framing options so _cssCornerCropHTML works.
      const vid = sp.currentVariantId;
      const allFraming = sp.variantConfigurators?.variants?.[vid]?.options?.framing
                      || sp.data?.options?.framing || [];
      const opt = allFraming.find(o => o.handle === curFraming);
      const color = (opt?.colors || []).find(c => c.handle === curColor);
      return {
        isCustom: true,
        isUnframed: false,
        preset: {
          resolvedVariantId: vid,
          resolvedFraming: curFraming,
          frameColorHandle: curColor,
          hex: color?.color_value || '#333',
          mouldingTile: color?.moulding_tile || '',
          mouldingWidth: color?.moulding_width || 15,
          border,
          name: this._frameDisplayName(curFraming, curColor, border, vid),
        },
      };
    }

    // ── Selection / size change ────────────────────────────────────────────

    // Full re-render only if the visible preset set or custom state changed;
    // otherwise patch prices/selection in-place to preserve existing mocks.
    _onSelectionChanged() {
      const sp = this._sp;
      const newPresets = this._getAvailablePresets();
      const newKeys = newPresets.map(p => this._presetKey(p));
      const customKey = this._isCustomActive()
        ? `custom|${sp.state?.confirmed?.framing_option}|${sp.state?.confirmed?.frame_color}|${sp.state?.confirmed?.border_option || 'no-border'}` : '';
      const sameSet = newKeys.length === this._renderedPresetKeys.length
        && newKeys.every((k, i) => k === this._renderedPresetKeys[i])
        && customKey === (this._lastRenderedCustomKey || '');
      if (sameSet) {
        this._updateSelection();
      } else {
        this._render();
      }
    }

    _onSizeChanged() {
      const sp = this._sp;
      const newSize = sp.state?.confirmed?.size_option;
      const curFraming = sp.state?.confirmed?.framing_option;

      if (newSize && curFraming && curFraming !== 'unframed') {
        // Keep the current frame if it's still purchasable at the new size
        // (either in top-3 presets or anywhere in the framing catalogue).
        const stillAvailable =
          this._getAvailablePresets().some(p => p.resolvedFraming === curFraming) ||
          this._isFramingAvailableForSize(curFraming, newSize);

        if (!stillAvailable) {
          // First choice: re-land the FAMILY, keep the colour and border.
          // sku-picker's selectFrameColor filters families by the confirmed
          // size and collapses colour tokens across them, so Black classic at
          // a box-only size becomes the Black box — no colour swap, no mount,
          // no medium switch. Only when that cannot produce a purchasable
          // line does a preset get imposed.
          const family = (sp.data?.options?.framing || []).find(o => o.handle === curFraming);
          const colObj = (family?.colors || []).find(c => c.handle === sp.state.confirmed.frame_color);
          const token = (colObj && sp._frameColorToken) ? sp._frameColorToken(colObj) : '';
          const relanded = token && sp.selectFrameColor?.(token)
            && sp._getSelectionDetails?.(sp.state.confirmed)?.available === true;

          if (!relanded) {
            // Switch to the first available recommendation ON THE CURRENT
            // VARIANT. Search all available presets (not just the top-3
            // slice), but never one from the other medium — the material sits
            // above the size in the precedence order (_applyPreset switches
            // sp.currentVariantId for cross-variant presets) — and never one
            // carrying a MOUNT: a paid upgrade must not arrive because the
            // shopper changed their size.
            // Never auto-select unframed — if nothing is found, keep current state.
            const fallback = this._getAllAvailablePresets()
              .find(p => p.resolvedVariantId === sp.currentVariantId
                && (p.border || 'no-border') !== 'mount');
            if (fallback) {
              this._applyPreset(fallback);
            }
          }
        }
      }

      this._onSelectionChanged();
    }

    // ── Apply a preset ─────────────────────────────────────────────────────

    _applyPreset(preset) {
      const sp = this._sp;
      sp.activeStyleName = preset.name || null;

      if (preset.resolvedVariantId !== sp.currentVariantId) {
        sp.currentVariantId = preset.resolvedVariantId;
        sp._loadVariantConfigurator(preset.resolvedVariantId);
        const artProduct = sp.variantConfigurators?.variants?.[preset.resolvedVariantId]?.artProduct;
        const productType = artProduct === 'canvas-print' ? 'canvas' : 'art_print';
        sp.container?.dispatchEvent(new CustomEvent('sku-picker:variant-changed', {
          bubbles: true,
          detail: { variantId: preset.resolvedVariantId, productType },
        }));
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', preset.resolvedVariantId);
          history.replaceState({}, '', url.toString());
        } catch {}
      }

      sp.state.confirmed.framing_option = preset.resolvedFraming;
      sp.state.confirmed.frame_color = preset.frameColorHandle;
      sp.state.confirmed.border_option = preset.border || 'no-border';
      if (preset.resolvedFraming === 'unframed' && sp.state.confirmed.border_option === 'mount') {
        sp.state.confirmed.border_option = 'no-border';
      }
      sp._updateConfirmedSelection?.(true);
    }

    // ── Corner mock HTML ───────────────────────────────────────────────────

    /** CSS corner-crop for the recommended trio cards.
     *  Zooms into the bottom-left corner of the pre-rendered frame-overlay image.
     *  `mock` = result of gallery.getProductMockForPreset({...}) or null.
     *  `artUrl` = URL string of the artwork image. */
    _cssCornerCropHTML(mock, artUrl, isWrap = false) {
      // Delegate to the shared module-level helper so the drawer can reuse it.
      return cssCornerCropHTML(mock, artUrl, isWrap);
    }

    _saveMocksToCache(items) {
      // No-op: the CSS corner-crop path no longer uses a canvas mock cache.
      // Kept for API compatibility in case external code references it.
    }

    /** Product orientation from the artwork aspect (for picking preset.mocks). */
    _artworkOrientation() {
      return artworkOrientation();
    }

    _artUrl() {
      // Prefer the SSR'd artwork URL — always present at first render. The live
      // hero <img> may not have a src yet on slower products (→ empty artwork).
      return document.querySelector('style-picker')?.dataset.artworkUrl
        || document.querySelector('[data-slide-role="frame-mock"] .mock-image__placement img')?.currentSrc
        || document.querySelector('[data-slide-role="frame-mock"] .mock-image__placement img')?.src
        || document.querySelector('.mock-image__placement img')?.src || '';
    }

    /** Find a preset's materialized mock for this orientation. Presets carry
     *  mocks via SSR (style_presets_json); the synthesized unframed/wrapped item
     *  borrows the matching unframed preset's mock so the whole trio is instant. */
    _presetMock(item, orientation) {
      const direct = item.preset?.mocks?.[orientation];
      if (direct?.frameImage) return direct;
      if (item.isUnframed) {
        const want = item.isCanvas ? 'canvas-print' : 'art-print';
        const up = this._stylePicker?.resolvedPresets?.find(
          (p) => p.resolvedFraming === 'unframed' && p.productType === want);
        const m = up?.mocks?.[orientation];
        if (m?.frameImage) return m;
      }
      return null;
    }

    async _mountMocks(items, seq) {
      const gallery = document.querySelector('media-gallery');
      const orientation = this._artworkOrientation();
      const artUrl = this._artUrl();
      const cards = Array.from(this.querySelectorAll('.surgical-frame-card'));

      // Phase 1 — INSTANT: render any card whose preset carries a materialized
      // mock for this orientation (SSR'd via style_presets_json). No network.
      const pending = [];
      items.forEach((item, i) => {
        if (this._mountSeq !== seq) return;
        const wrap = cards[i]?.querySelector('.thumb');
        if (!wrap) return;
        const presetMock = this._presetMock(item, orientation);
        if (presetMock) {
          const isWrap = (item.preset?.resolvedFraming || (item.isUnframed ? 'unframed' : '')) === 'unframed';
          wrap.innerHTML = this._cssCornerCropHTML(presetMock, artUrl, isWrap);
        } else {
          pending.push({ item, i });
        }
      });
      if (!pending.length) return; // whole trio rendered from SSR — no fetch

      // Phase 2 — FALLBACK: configurator fetch only for cards without a
      // materialized mock (e.g. a preset missing this orientation, like square
      // box-frame). Spans art + canvas variants.
      if (gallery?.ensureAllMocksLoaded) await gallery.ensureAllMocksLoaded();
      else await gallery?.ensureFullMocksLoaded?.();
      if (this._mountSeq !== seq) return;

      const artUrl2 = this._artUrl() || artUrl;
      pending.forEach(({ item, i }) => {
        if (this._mountSeq !== seq) return;
        const wrap = cards[i]?.querySelector('.thumb');
        if (!wrap) return;
        const p = item.preset || (item.isUnframed ? {
          resolvedVariantId: this._sp.currentVariantId,
          frameColorHandle:  null,
          border:            this._sp.state?.confirmed?.border_option || 'no-border',
          resolvedFraming:   'unframed',
        } : null);
        const mock = p ? (gallery?.getProductMockForPreset?.({
          resolvedVariantId: p.resolvedVariantId,
          frameColorHandle:  p.frameColorHandle,
          border:            p.border || 'no-border',
          resolvedFraming:   p.resolvedFraming,
        }) || null) : null;
        const isWrap = p?.resolvedFraming === 'unframed';
        wrap.innerHTML = this._cssCornerCropHTML(mock, artUrl2, isWrap);
      });
    }

    // ── Render ─────────────────────────────────────────────────────────────

    _render() {
      if (!this._stylePicker) return;
      const fmt = getCurrencyFmt();
      const sp = this._sp;
      const recommended = this._getAvailablePresets();
      const curFraming = sp.state?.confirmed?.framing_option;
      const allFraming = sp.variantConfigurators?.variants?.[sp.currentVariantId]?.options?.framing
                      || sp.data?.options?.framing || [];
      const currentSize = sp.state?.confirmed?.size_option;

      // Count framing×color options available for the current size across all
      // variants — mirrors exactly the card count in the All Frames drawer tab.
      const _allVarEntries = Object.entries(sp.variantConfigurators?.variants || {});
      let totalFrames = 0;
      if (_allVarEntries.length) {
        const _seen = new Set();
        const _ordered = [
          ..._allVarEntries.filter(([id]) => id === sp.currentVariantId),
          ..._allVarEntries.filter(([id]) => id !== sp.currentVariantId),
        ];
        for (const [, vd] of _ordered) {
          for (const f of (vd.options?.framing || [])) {
            if (f.handle === 'unframed' || _seen.has(f.handle)) continue;
            _seen.add(f.handle);
            if (currentSize && !vd.framing_pricing?.some(e => e.size_option === currentSize && e.framing_option === f.handle)) continue;
            totalFrames += f.colors?.length || 1;
          }
        }
      } else {
        totalFrames = allFraming.filter(f => f.handle !== 'unframed')
          .reduce((s, f) => s + (f.colors?.length || 1), 0);
      }
      // Arm-split (PR #165 review): the 3-swatch dynamic-exclude pill is the
      // treatment design; control keeps prod's up-to-6 swatches.
      const swatchSources = document.querySelector('.pdp-carousel-on')
        ? this._getAvailableSwatchSources(3, recommended.map(p => p.frameColorHandle))
        : this._getAvailableSwatchSources(6);

      // Current display name: preset name if matched, else fallback to option labels
      const PRINT_ONLY_LABEL = T('frame.format_unframed', 'Print only');
      let curDisplayName = PRINT_ONLY_LABEL;
      let curAdd = this._getUnframedAdd();
      if (curFraming && curFraming !== 'unframed') {
        const curColor = sp.state?.confirmed?.frame_color;
        const curBorder = sp.state?.confirmed?.border_option || 'no-border';
        curDisplayName = this._frameDisplayName(curFraming, curColor, curBorder, sp.currentVariantId);
        curAdd = styleDeltaFor(sp, sp.state?.confirmed?.size_option, {
          variantId: sp.currentVariantId,
          framing: curFraming,
          border: curBorder,
        }) ?? 0;
      }

      // 2c55: the "no extra frame" choice for a canvas product is still
      // valid — the canvas itself is wrapped on a wooden stretcher — it
      // just shouldn't be called "Print only" or "Unframed". Show it as
      // "Wrapped Canvas" when the current variant is canvas, "Print only"
      // for art prints.
      const currentIsCanvas = this._productTypeFor(sp.currentVariantId) === 'canvas';
      const unframedItem = { isUnframed: true, isCanvas: currentIsCanvas };

      // When a custom frame is active, show it at position 0 and limit recommended
      // to 2 so the total card count stays at 4 (custom + 2 presets + unframed).
      const isCustom = this._isCustomActive();
      const customItem = isCustom ? this._getCustomItem() : null;
      const recommendedItems = (isCustom ? recommended.slice(0, 2) : recommended).map((p, i) => ({
        preset: p,
        isUnframed: false,
        isRecommended: i === 0 && !isCustom,
      }));
      const allItems = [
        ...(customItem ? [customItem] : []),
        ...recommendedItems,
        unframedItem,
      ];

      // Stash any already-rendered mocks into the cache before wiping innerHTML
      if (this._lastAllItems?.length) this._saveMocksToCache(this._lastAllItems);
      this._lastAllItems = allItems;
      this._renderedPresetKeys = recommended.map(p => this._presetKey(p));
      this._lastRenderedCustomKey = isCustom
        ? `custom|${sp.state?.confirmed?.framing_option}|${sp.state?.confirmed?.frame_color}|${sp.state?.confirmed?.border_option || 'no-border'}` : '';
      this._mountSeq = (this._mountSeq || 0) + 1;
      const mountSeq = this._mountSeq;

      const FRAME_LABEL_TEXT = T('frame.label', 'Frame');
      const ABOUT_STYLE_TEXT = T('frame.about_style', 'About this style');
      const RECOMMENDED_TEXT = T('frame.recommended', 'Recommended');
      const VIEW_ALL_LEAD = T('frame.view_all_lead', 'View all');
      const VIEW_ALL_FRAMES = T('frame.view_all_frames', 'frames');
      const VIEW_ALL_TAIL = T('frame.view_all_tail', '— including custom builder');
      this.innerHTML = `
        <div class="surgical-frame">
          <div class="surgical-frame__head">
            <span class="surgical-frame__label">
              ${FRAME_LABEL_TEXT} · <b>${curDisplayName}</b>
              ${curAdd ? `<span class="muted">+${fmt(curAdd)}</span>` : ''}
            </span>
            <button type="button" class="surgical-frame__about" data-pdp-about-style${curFraming === 'unframed' ? ' hidden' : ''}>${ABOUT_STYLE_TEXT}</button>
          </div>
          <div class="surgical-frame__rec">
            ${allItems.map((item) => {
              const { preset, isUnframed, isCanvas, isCustom: isCustomCard, isRecommended } = item;
              const isOn = isUnframed
                ? curFraming === 'unframed'
                : preset.resolvedFraming === curFraming
                  && preset.frameColorHandle === sp.state?.confirmed?.frame_color
                  && (preset.border || 'no-border') === (sp.state?.confirmed?.border_option || 'no-border');
              const add = isUnframed ? this._getUnframedAdd() : this._getFrameAdd(preset);
              const delta = add ? `+${fmt(add)}` : '';
              // 2c55: canvas products show "Wrapped Canvas" not "Print only" /
              // "Unframed" — canvas is mounted on a wooden stretcher regardless.
              const unframedLabel = isCanvas
                ? T('frame.format_wrapped_canvas', 'Wrapped Canvas')
                : PRINT_ONLY_LABEL;
              const name = isUnframed ? unframedLabel : preset.name;
              return `
                <button type="button" class="surgical-frame-card ${isOn ? 'on' : ''}${isCustomCard ? ' custom' : ''}">
                  ${isRecommended ? `<div class="rec">${RECOMMENDED_TEXT}</div>` : ''}
                  <div class="thumb"></div>
                  <div class="name">${name}</div>
                  ${delta ? `<div class="delta">${delta}</div>` : ''}
                </button>`;
            }).join('')}
          </div>
          <button type="button" class="surgical-frame__all" data-pdp-frame-all>
            <span class="left">
              <span>${VIEW_ALL_LEAD} <b>${totalFrames} ${VIEW_ALL_FRAMES}</b> ${VIEW_ALL_TAIL}</span>
            </span>
            ${swatchSources.length ? `
            <span class="surgical-frame__all-swatches" aria-hidden="true">
              ${swatchSources.map(s => `<span class="surgical-frame__all-swatch" style="${s.tile ? `background-image:url('${s.tile}');background-size:cover;background-position:center` : `background-color:${s.hex}`}"></span>`).join('')}
            </span>` : ''}
            <span class="arr">→</span>
          </button>
        </div>`;

      this._wire(allItems);
      this._mountMocks(allItems, mountSeq);
    }

    _updateSelection() {
      if (!this._stylePicker) return;
      const fmt = getCurrencyFmt();
      const sp = this._sp;
      const curFraming = sp.state?.confirmed?.framing_option;
      const curColor = sp.state?.confirmed?.frame_color;
      const recommended = this._getAvailablePresets();

      const labelB = this.querySelector('.surgical-frame__label b');
      const labelMuted = this.querySelector('.surgical-frame__label .muted');
      if (labelB) {
        // 2c55: canvas variants show "Wrapped Canvas" not "Print only".
        const currentIsCanvas = this._productTypeFor(sp.currentVariantId) === 'canvas';
        let name = currentIsCanvas
          ? T('frame.format_wrapped_canvas', 'Wrapped Canvas')
          : T('frame.format_unframed', 'Print only');
        let add = this._getUnframedAdd();
        if (curFraming && curFraming !== 'unframed') {
          const curBorder = sp.state?.confirmed?.border_option || 'no-border';
          name = this._frameDisplayName(curFraming, curColor, curBorder, sp.currentVariantId);
          add = styleDeltaFor(sp, sp.state?.confirmed?.size_option, {
            variantId: sp.currentVariantId,
            framing: curFraming,
            border: curBorder,
          }) ?? 0;
        }
        labelB.textContent = name;
        if (labelMuted) labelMuted.textContent = add ? `+${fmt(add)}` : '';
      }

      // Rebuild allItems the same way as _render() so card indices stay in sync.
      const isCustom = this._isCustomActive();
      const customItem = isCustom ? this._getCustomItem() : null;
      const recommendedItems = (isCustom ? recommended.slice(0, 2) : recommended).map(p => ({
        preset: p, isUnframed: false,
      }));
      const allItems = [
        ...(customItem ? [customItem] : []),
        ...recommendedItems,
        { isUnframed: true },
      ];

      this.querySelectorAll('.surgical-frame-card').forEach((card, i) => {
        const item = allItems[i];
        if (!item) return;
        const isOn = item.isUnframed
          ? curFraming === 'unframed'
          : item.preset.resolvedFraming === curFraming
            && item.preset.frameColorHandle === curColor
            && (item.preset.border || 'no-border') === (sp.state?.confirmed?.border_option || 'no-border');
        card.classList.toggle('on', isOn);
        const deltaEl = card.querySelector('.delta');
        if (deltaEl) {
          const add = item.isUnframed ? this._getUnframedAdd() : this._getFrameAdd(item.preset);
          deltaEl.textContent = add ? `+${fmt(add)}` : '';
        }
      });
      const aboutBtn = this.querySelector('.surgical-frame__about');
      if (aboutBtn) aboutBtn.hidden = curFraming === 'unframed';
    }

    _wire(allItems) {
      this.querySelectorAll('.surgical-frame-card').forEach((btn, i) => {
        btn.addEventListener('click', () => {
          const item = allItems[i];
          if (!item) return;
          if (item.isCustom) {
            // Custom card is already the active selection — re-confirm to sync
            // downstream state (price, gallery mock, sticky CTA).
            this._sp._updateConfirmedSelection?.(true);
          } else if (item.isUnframed) {
            const sp = this._sp;
            sp.state.confirmed.framing_option = 'unframed';
            if (sp.state.confirmed.border_option === 'mount') sp.state.confirmed.border_option = 'no-border';
            sp._updateConfirmedSelection?.(true);
            emitPresetSelected({ presetId: 'unframed', framing: 'unframed', source: 'row' });
          } else {
            this._applyPreset(item.preset);
            const p = item.preset;
            emitPresetSelected({
              presetId: p.id, presetName: p.name, framing: p.resolvedFraming,
              frameColor: p.frameColorHandle, border: p.border, source: 'row',
            });
          }
        });
      });
      // "View all N frames" is handled by the module-level delegated click
      // handler (→ drawer.open()), which snapshots _build and renders before
      // showing. A direct showModal() here would bypass that and double-open.
    }
  }
  customElements.define('pdp-surgical-frame-picker', SurgicalFramePicker);

  // ============================================================
  // SHARED CSS-MOCK HELPERS — used by both Picker and Drawer
  // ============================================================

  /** Compute the corner-crop inner positioning from the mock's placement.
   *  Frame mock images are a fixed SQUARE canvas with the framed artwork centred
   *  and orientation-dependent padding, so the frame's bottom-left corner sits
   *  at a different spot per aspect ratio (portrait/square/landscape). Derive
   *  that corner from the artwork placement + moulding (the padding on the axis
   *  the frame bleeds to) and anchor it to a fixed card position — one formula
   *  crops all three orientations identically, no per-aspect magic offsets. */
  function cornerCropInnerStyle(mock) {
    // Canvas mocks store aspectRatio as "auto" — fall back to the real artwork
    // image's aspect (the same artwork across every mock) so the artwork-bottom
    // calc is correct; defaulting to 1 mis-positions non-square canvas crops.
    let aspect = parseFloat(mock.aspectRatio);
    // Canvas mocks store "auto" → use the SSR'd artwork aspect (reliable at first
    // render). Falling back to the hero <img>'s naturalWidth mis-positions the
    // crop in Safari, where the image often isn't loaded yet on first paint.
    if (!aspect || !isFinite(aspect)) {
      aspect = parseFloat(document.querySelector('style-picker')?.dataset.artworkAspect || '');
    }
    if (!aspect || !isFinite(aspect)) {
      const ai = document.querySelector('media-gallery .mock-image__placement img');
      aspect = (ai && ai.naturalWidth && ai.naturalHeight) ? ai.naturalWidth / ai.naturalHeight : 1;
    }
    const pl = parseFloat(mock.placementLeft) || 0;
    const pt = parseFloat(mock.placementTop) || 0;
    const pw = parseFloat(mock.placementWidth) || 100;
    const ax = pl / 100;                  // artwork bottom-left corner, x fraction
    const ay = (pt + pw / aspect) / 100;  // artwork bottom-left corner, y fraction
    // Anchor the ARTWORK corner — the one position we can derive reliably for
    // every aspect (portrait/square/landscape) — at a fixed card spot, with the
    // moulding filling down-left of it. Frame mock backdrops are white, so the
    // .surgical-cc white background blends any padding/void seamlessly (square
    // mocks don't bleed the frame to the edge, so a little backdrop shows).
    // TX/TY are tuned so the moulding corner lands bottom-left for the bleeding
    // aspects, matching the portrait/landscape look that already read well.
    const K = 4.8, TX = 0.28, TY = 0.72;
    const left = (TX - ax * K) * 100;
    const top = (TY - ay * K) * 100;
    const dim = K * 100;
    // Height via aspect-ratio (from the definite width), NOT percentage height:
    // the card is square, so this is identical in Chrome, but Safari resolves a
    // %-height against the aspect-ratio-sized container to 0 (collapsing the
    // frame and letting the artwork fill the card).
    return `left:${left.toFixed(2)}%;top:${top.toFixed(2)}%;bottom:auto;width:${dim}%;height:auto;aspect-ratio:1;`;
  }

  /** Reuse the hero artwork's exact in-DOM URL for same-file requests at card
   *  sizes — a guaranteed browser-cache hit instead of refetching the artwork
   *  at a unique size. Skipped for larger targets (the 900px BYO panel renders
   *  at ~900+ device px, where the hero's 540px variant is visibly soft). */
  function heroArtworkReuse(url, width) {
    if (width > 700) return null;
    const hero = document.querySelector('img.mock-image__artwork');
    const heroSrc = hero && (hero.currentSrc || hero.src);
    if (!heroSrc) return null;
    const path = (u) => u.split('?')[0].replace(/^(https?:)?\/\//, '');
    return path(heroSrc) === path(url) ? heroSrc : null;
  }

  /** Normalize a Shopify CDN image URL to a target width + quality, overriding
   *  any stored params. Frame mocks come from mixed sources (preset mocks store
   *  full-res URLs; configurator mocks store width=1600) — without this the
   *  trio loads multi-hundred-KB full-res frames (e.g. a 439KB canvas texture). */
  function cdnImg(url, width, quality = 70) {
    if (!url || typeof url !== 'string' || !url.includes('/')) return url || '';
    const reused = heroArtworkReuse(url, width);
    if (reused) return reused;
    const [base, qs = ''] = url.split('?');
    const params = new URLSearchParams(qs);
    params.delete('width');
    params.delete('height');
    params.set('width', String(width));
    params.set('quality', String(quality));
    return `${base}?${params.toString()}`;
  }

  /** CSS corner-crop thumbnail (bottom-left corner zoom).
   *  `mock` = result of gallery.getProductMockForPreset({...}) or null.
   *  Unframed/canvas are NOT bare — they have real mocks; bare is only the
   *  genuine fallback when no mock exists for the combo.
   *
   *  Image sizing: the corner crop SCALES the source up ~3x and clips, so the
   *  frame renders at 2-3x its slot width (878 CSS px in the all-frames
   *  drawer at 412px viewport). Frames are flat-colour bleed images — webp at
   *  width=1600 is ~7 KB — so they always get 1600 (sharp at any zoom).
   *  Artwork is photographic (width=750 of this file is ~112 KB), so callers
   *  pass artWidth by context: 700 for the always-visible trio cards (the
   *  hero-URL reuse in cdnImg keeps those at zero network cost) and 1000 for
   *  the on-demand drawer, where quality outranks bytes. */
  function cssCornerCropHTML(mock, artUrl, _isWrap = false, artWidth = 700) {
    // NOTE: no loading="lazy" — the corner crop positions images off-screen, so
    // Safari's lazy-loader never fires for them (they stay 0x0 → invisible).
    if (!mock || !mock.frameImage) {
      return `<div class="surgical-cc surgical-cc--bare">
        <div class="surgical-cc__inner">
          <img class="surgical-cc__art-bare" src="${cdnImg(artUrl, artWidth)}" alt="">
        </div></div>`;
    }
    return `<div class="surgical-cc">
      <div class="surgical-cc__inner" style="${cornerCropInnerStyle(mock)}">
        <img class="surgical-cc__frame" src="${cdnImg(mock.frameImage, 1600, 75)}" alt="">
        <div class="surgical-cc__art" style="top:${mock.placementTop}%;left:${mock.placementLeft}%;width:${mock.placementWidth}%;transform:${mock.transform || 'none'};">
          <img src="${cdnImg(artUrl, artWidth)}" alt="">
        </div>
      </div>
    </div>`;
  }

  /** Full (non-cropped) CSS mock — for the BYO preview panel.
   *  Unframed/canvas use their real mocks; bare is only the no-mock fallback. */
  function cssFullMockHTML(mock, artUrl) {
    if (!mock || !mock.frameImage) {
      return `<div class="surgical-cc surgical-cc--bare surgical-cc--full">
        <div class="surgical-cc__inner"><img class="surgical-cc__art-bare" src="${cdnImg(artUrl, 900)}" alt="" loading="lazy"></div></div>`;
    }
    // Match the hero (mock-image.liquid): the placement box gets the mock's
    // aspect ratio so the artwork fills the frame opening (no gap), rather than
    // falling back to the artwork's natural aspect.
    const arStyle = (mock.aspectRatio && mock.aspectRatio !== 'auto')
      ? `aspect-ratio:${mock.aspectRatio};` : '';
    return `<div class="surgical-cc surgical-cc--full">
      <div class="surgical-cc__inner surgical-cc__inner--full">
        <img class="surgical-cc__frame" src="${cdnImg(mock.frameImage, 900)}" alt="" loading="lazy">
        <div class="surgical-cc__art" style="top:${mock.placementTop}%;left:${mock.placementLeft}%;width:${mock.placementWidth}%;transform:${mock.transform || 'none'};${arStyle}">
          <img src="${cdnImg(artUrl, 900)}" alt="" loading="lazy">
        </div>
      </div>
    </div>`;
  }

  /** Resolve the page artwork URL from the CSS hero mock-image. */
  function resolveArtUrl() {
    return document.querySelector('[data-slide-role="frame-mock"] .mock-image__placement img')?.currentSrc ||
      document.querySelector('[data-slide-role="frame-mock"] .mock-image__placement img')?.src ||
      document.querySelector('.mock-image__placement img')?.src || '';
  }

  // ============================================================
  // FRAME DRAWER — All Frames + Build Your Own
  // ============================================================
  const COLOR_HEX = {
    black: '#2d2d2d', white: '#f5f0e8', natural: '#c4a574',
    brown: '#6b4226', gold: '#c9a84c', silver: '#b8b8b8',
  };

  class SurgicalFrameDrawer extends HTMLElement {
    constructor() { super(); this._unsubs = []; this._tab = 'all'; this._build = null; }
    connectedCallback() { whenSkuPickerReady(sp => this._init(sp)); }
    disconnectedCallback() { this._unsubs.forEach(fn => fn()); this._unsubs = []; }

    _init(sp) {
      this.sp = sp;
      // Don't render until opened — corner mocks are expensive to mount.
      // Render an empty <dialog> shell so .querySelector('dialog') works.
      // tabindex=-1 on the dialog so .showModal() auto-focuses the dialog
      // itself rather than the first interactive child (the "All Frames"
      // tab), which would otherwise paint a :focus-visible outline that
      // reads as a stray rectangular box around the tab on first open (a9f2).
      this.innerHTML = '<dialog class="surgical-fd" tabindex="-1"><div class="surgical-fd-inner"></div></dialog>';
      const onSel = () => { if (this.isOpen()) this._refreshSelections(); };
      document.addEventListener('sku-picker:selection-changed', onSel);
      this._unsubs.push(() => document.removeEventListener('sku-picker:selection-changed', onSel));
    }

    isOpen() { return this.querySelector('dialog')?.hasAttribute('open'); }

    open(tab = 'all') {
      this._tab = tab;
      this._build = this._snapshotBuild();
      this._render();
      const dlg = this.querySelector('dialog');
      if (dlg && typeof dlg.showModal === 'function') {
        dlg.showModal();
        // Move focus to the dialog itself so the first tab doesn't pick up
        // the :focus-visible ring on initial show (a9f2). Safari applies
        // :focus-visible synchronously during showModal() before our focus
        // override runs, so defer to the next frame.
        requestAnimationFrame(() => {
          try { dlg.focus({ preventScroll: true }); } catch (_e) { /* no-op */ }
        });
        document.body.style.overflow = 'hidden';
        // Analytics: reuse the legacy style-picker event so posthog-events.js
        // captures style_sheet_opened for THIS drawer too — the surgical
        // "View all N frames" drawer never dispatched it, which is why
        // style_sheet_opened flatlined (~2/wk) while preset taps ran ~10k/wk.
        document.dispatchEvent(new CustomEvent('style-picker:sheet-opened', {
          detail: { initialTab: tab, surface: 'surgical-frames-drawer' },
        }));
      }
    }

    close() {
      const dlg = this.querySelector('dialog');
      if (dlg && typeof dlg.close === 'function') dlg.close();
      document.body.style.overflow = '';
    }

    _snapshotBuild() {
      const sp = this.sp;
      const variant = sp.variantConfigurators?.variants?.[sp.currentVariantId];
      const productType = variant?.artProduct === 'canvas-print' ? 'canvas' : 'art_print';
      return {
        productType, // 'art_print' | 'canvas'
        framing: sp.state.confirmed.framing_option || 'unframed',
        frame_color: sp.state.confirmed.frame_color || '',
        border: sp.state.confirmed.border_option || 'no-border',
      };
    }

    _allFramings() {
      const sp = this.sp;
      const allEntries = Object.entries(sp.variantConfigurators?.variants || {});
      if (!allEntries.length) return sp.data?.options?.framing || [];
      // Union across all variants; current-variant framings take priority for metadata.
      const seen = new Set();
      const result = [];
      const ordered = [
        ...allEntries.filter(([id]) => id === sp.currentVariantId),
        ...allEntries.filter(([id]) => id !== sp.currentVariantId),
      ];
      for (const [variantId, vd] of ordered) {
        for (const f of (vd.options?.framing || [])) {
          if (!seen.has(f.handle)) {
            seen.add(f.handle);
            result.push({ ...f, _variantId: variantId });
          }
        }
      }
      return result;
    }

    // Drawer thumbnail placeholder — filled async by _mountDrawerMocks().
    // Keeps the data attributes so _mountDrawerMocks can resolve the mock.
    _buildCornerMockHTML(framingHandle, color, productType, border = 'no-border') {
      const isUnframed = framingHandle === 'unframed';
      // Render a bare-art placeholder immediately; _mountDrawerMocks replaces it.
      const artUrl = resolveArtUrl();
      return `<div class="surgical-cc surgical-cc--bare surgical-cc--loading"
        data-framing="${framingHandle}"
        data-color-handle="${color?.handle || ''}"
        data-product-type="${productType}"
        data-border="${border}"
        data-is-unframed="${isUnframed}">
        <div class="surgical-cc__inner"><img class="surgical-cc__art-bare" src="${artUrl}" alt="" loading="lazy"></div>
      </div>`;
    }

    // Full preview placeholder — filled async by _mountBYOMock().
    // Keeps data attrs so _mountBYOMock can resolve the correct mock.
    _buildFullMockHTML(framingHandle, color, productType, border = 'no-border') {
      const isUnframed = framingHandle === 'unframed';
      const artUrl = resolveArtUrl();
      return `<div class="surgical-cc surgical-cc--bare surgical-cc--full surgical-cc--loading"
        data-framing="${framingHandle}"
        data-color-handle="${color?.handle || ''}"
        data-product-type="${productType}"
        data-border="${border}"
        data-is-unframed="${isUnframed}">
        <div class="surgical-cc__inner"><img class="surgical-cc__art-bare" src="${artUrl}" alt="" loading="lazy"></div>
      </div>`;
    }

    // Compute all data needed for the BYO custom tab + footer
    _computeBuildData() {
      const sp = this.sp;
      const all = this._allFramings();
      const sz = sp.state.confirmed.size_option;
      const build = this._build || this._snapshotBuild();
      const fmt = getCurrencyFmt();

      // Look up the variant data for a given productType string
      const variantForType = (type) =>
        Object.values(sp.variantConfigurators?.variants || {}).find(vd =>
          (vd.artProduct === 'canvas-print' ? 'canvas' : 'art_print') === type
        );

      const formatOptions = [
        { id: 'art_print', label: T('frame.format_print',  'Art print') },
        { id: 'canvas',    label: T('frame.format_canvas', 'Canvas') },
      ].map(o => {
        const vd = variantForType(o.id);
        const available = !sz || !vd || vd.size_pricing?.some(e => e.size_option === sz);
        return { ...o, available: available !== false };
      });

      const buildVariantEntry = Object.entries(sp.variantConfigurators?.variants || {})
        .find(([, vd]) => (vd.artProduct === 'canvas-print' ? 'canvas' : 'art_print') === build.productType);
      const buildVariantId = buildVariantEntry?.[0];
      const buildVariant = buildVariantEntry?.[1] || variantForType(build.productType);
      const styleOptions = all.filter(f => f.handle !== 'unframed').map(f => {
        const available = !sz || !buildVariant ||
          buildVariant.framing_pricing?.some(e => e.size_option === sz && e.framing_option === f.handle);
        return { id: f.handle, label: f.label || f.name || f.short_name || f.handle, sub: f.description, available: !!available };
      });

      // Source the border list from the BUILD variant, not the currently-
      // loaded one — otherwise switching format (e.g. Canvas → Art print)
      // keeps the old variant's borders (canvas exposes only "no-border").
      const borderOptions = (buildVariant?.options?.border || sp.data?.options?.border || []).map(b => {
        const available = b.handle === 'no-border' || !sz || !buildVariant ||
          buildVariant.border_pricing?.some(e => e.size_option === sz && e.border_option === b.handle);
        return { id: b.handle, label: b.label || b.name || b.short_name || b.handle, available: !!available };
      });

      // Auto-correct build state if current selections are unavailable for the size
      if (formatOptions.length && !formatOptions.find(o => o.id === build.productType)?.available) {
        build.productType = formatOptions.find(o => o.available)?.id || build.productType;
      }
      if (build.framing !== 'unframed' && !styleOptions.find(o => o.id === build.framing)?.available) {
        build.framing = styleOptions.find(o => o.available)?.id || 'unframed';
        // Pick a valid colour for the new framing. Leaving it empty makes the
        // mock lookup (which keys on colour) miss and renders a bare print —
        // e.g. switching art-print → canvas auto-corrects the framing handle
        // but left no colour, so the canvas showed as a frameless artwork.
        const newColors = all.find(f => f.handle === build.framing)?.colors || [];
        build.frame_color = newColors[0]?.handle || '';
      }
      if (build.border !== 'no-border' && !borderOptions.find(o => o.id === build.border)?.available) {
        build.border = 'no-border';
      }

      // colorOptions are computed AFTER the auto-correction above so they
      // reflect the (possibly corrected) framing — otherwise the preview/footer
      // resolve against the old framing's colours and the mock lookup misses.
      const colorOptions = (all.find(f => f.handle === build.framing)?.colors || []).map(c => ({
        id: c.handle,
        label: c.short_name,
        hex: c.color_value || COLOR_HEX[c.short_name?.toLowerCase()] || '#333',
        available: true, // colors share framing availability; pricing is at framing level
      }));

      const buildSelectedColor = colorOptions.find(c => c.id === build.frame_color) || colorOptions[0];
      const currentColorObj = (all.find(f => f.handle === build.framing)?.colors || [])
        .find(c => c.handle === buildSelectedColor?.id);
      // sp.components only holds pricing for the CURRENT variant. When the
      // BYO format differs from it (e.g. art-print PDP, Canvas selected),
      // pull deltas from the build variant's own pricing tables instead —
      // otherwise the component lookup misses and the footer shows +0.
      const buildIsCurrent = !buildVariantId || buildVariantId === sp.currentVariantId;
      const buildFraming = buildIsCurrent
        ? sp.components?.framing?.[sz]?.[build.framing]
        : buildVariant?.framing_pricing?.find(e => e.size_option === sz && e.framing_option === build.framing);
      const buildBorder = buildIsCurrent
        ? sp.components?.border?.[sz]?.[build.border]
        : buildVariant?.border_pricing?.find(e => e.size_option === sz && e.border_option === build.border);
      // Footer delta vs the unframed print (see REFERENCE PRICING): a canvas
      // build carries the canvas-vs-print base difference on top of the
      // framing/border adds, so "+€X" here matches how the total will move.
      const printBase = printBasePriceFor(sp, sz);
      const buildBase = baseSizePrice(buildVariant, sz);
      const buildBaseDelta = (printBase != null && buildBase != null) ? buildBase - printBase : 0;
      const buildTotal = buildBaseDelta + (buildFraming?.price || 0) + (buildBorder?.price || 0);
      return { build, fmt, formatOptions, styleOptions, colorOptions, borderOptions, buildSelectedColor, currentColorObj, buildTotal };
    }

    // Returns innerHTML for the BYO tab body (preview + steps, no footer)
    _buildCustomBodyHTML(data) {
      const { build, formatOptions, styleOptions, colorOptions, borderOptions, buildSelectedColor, currentColorObj } = data;
      const previewMock = this._buildFullMockHTML(
        build.framing, currentColorObj, build.productType, build.border
      );
      const fmtLabel = (s) => (s || '').toLocaleUpperCase();
      const PRINT_ONLY = T('frame.format_unframed', 'Print only');
      return `
        <div class="surgical-fd-preview">${previewMock}</div>

        <div class="surgical-fd-step">
          <div class="surgical-fd-step-head">${fmtLabel(T('frame.format', 'Format'))} <b>${formatOptions.find(o => o.id === build.productType)?.label || ''}</b></div>
          <div class="surgical-fd-options">
            ${formatOptions.map(o => `
              <button type="button" class="surgical-fd-opt ${o.id === build.productType ? 'on' : ''}" data-step="format" data-value="${o.id}" ${!o.available ? 'disabled' : ''}>
                ${o.label}
              </button>`).join('')}
          </div>
        </div>

        <div class="surgical-fd-step">
          <div class="surgical-fd-step-head">${fmtLabel(T('frame.style', 'Frame style'))} <b>${styleOptions.find(o => o.id === build.framing)?.label || (build.productType === 'canvas' ? T('frame.format_wrapped_canvas', 'Wrapped Canvas') : PRINT_ONLY)}</b></div>
          <div class="surgical-fd-options">
            <button type="button" class="surgical-fd-opt ${build.framing === 'unframed' ? 'on' : ''}" data-step="framing" data-value="unframed">${build.productType === 'canvas' ? T('frame.format_wrapped_canvas', 'Wrapped Canvas') : PRINT_ONLY}</button>
            ${styleOptions.map(o => `
              <button type="button" class="surgical-fd-opt ${o.id === build.framing ? 'on' : ''}" data-step="framing" data-value="${o.id}" ${!o.available ? 'disabled' : ''}>
                ${o.label}
              </button>`).join('')}
          </div>
        </div>

        ${build.framing !== 'unframed' && colorOptions.length ? `
        <div class="surgical-fd-step">
          <div class="surgical-fd-step-head">${fmtLabel(T('frame.colour', 'Colour'))} <b>${buildSelectedColor?.label || ''}</b></div>
          <div class="surgical-fd-options">
            ${colorOptions.map(o => `
              <button type="button" class="surgical-fd-opt ${o.id === build.frame_color ? 'on' : ''}" data-step="color" data-value="${o.id}">
                <span class="swatch" style="background:${o.hex}"></span>${o.label}
              </button>`).join('')}
          </div>
        </div>` : ''}

        ${borderOptions.length ? `
        <div class="surgical-fd-step">
          <div class="surgical-fd-step-head">${fmtLabel(T('frame.border', 'Border'))} <b>${borderOptions.find(o => o.id === build.border)?.label || T('frame.border_none', 'None')}</b></div>
          <div class="surgical-fd-options">
            ${borderOptions.map(o => `
              <button type="button" class="surgical-fd-opt ${o.id === build.border ? 'on' : ''}" data-step="border" data-value="${o.id}" ${!o.available ? 'disabled' : ''}>
                ${o.label}
              </button>`).join('')}
          </div>
        </div>` : ''}
      `;
    }

    // Returns innerHTML for the pinned footer (summary + apply button)
    _buildFootHTML(data) {
      const { build, fmt, formatOptions, styleOptions, buildSelectedColor, buildTotal } = data;
      // 2c55: canvas variants say "Wrapped Canvas" when no extra frame
      // is selected, not "Print only".
      const unframedLabel = build.productType === 'canvas'
        ? T('frame.format_wrapped_canvas', 'Wrapped Canvas')
        : T('frame.format_unframed', 'Print only');
      return `
        <div class="summary">
          <span>${formatOptions.find(o => o.id === build.productType)?.label || ''} · ${styleOptions.find(o => o.id === build.framing)?.label || unframedLabel}${buildSelectedColor && build.framing !== 'unframed' ? ' (' + buildSelectedColor.label + ')' : ''}</span>
          <b>+${fmt(buildTotal)}</b>
        </div>
        <button type="button" class="surgical-fd-apply" data-apply>${T('frame.apply', 'Apply')}</button>
      `;
    }

    _render() {
      const sp = this.sp;
      const all = this._allFramings();
      const sz = sp.state.confirmed.size_option;
      const variant = sp.variantConfigurators?.variants?.[sp.currentVariantId];
      const productType = variant?.artProduct === 'canvas-print' ? 'canvas' : 'art_print';
      const curFraming = sp.state.confirmed.framing_option;
      const curColor = sp.state.confirmed.frame_color;
      // Mirror the preset names the surgical frame picker uses ("The Organic",
      // "The Bold" etc.) instead of the raw "framing · color" pair so the
      // picker card label matches the modal card label for the same frame.
      const presets = document.querySelector('style-picker')?.resolvedPresets || [];

      // View All lists the curated PRESETS (grouped by framing family), each
      // rendered from its SSR'd preset.mock for this orientation — not the full
      // framing×colour catalogue. (BYO covers arbitrary combos.)
      const aspectAttr = parseFloat(document.querySelector('style-picker')?.dataset.artworkAspect || '');
      const orientation = (!aspectAttr || !isFinite(aspectAttr)) ? 'portrait'
        : (aspectAttr < 0.95 ? 'portrait' : aspectAttr > 1.05 ? 'landscape' : 'square');
      const artUrl = resolveArtUrl();

      const byFraming = new Map();
      for (const p of presets) {
        const key = p.resolvedFraming || p.framing || 'unframed';
        if (!byFraming.has(key)) byFraming.set(key, []);
        byFraming.get(key).push(p);
      }

      const groupedHTML = [...byFraming.entries()].map(([framingHandle, group]) => {
        // Only presets purchasable at the CURRENT size (QA thread — the modal
        // was listing frames unavailable at the selected size). styleDeltaFor
        // returns null when the size×framing combination isn't priced.
        const purchasable = group.filter(p => p.resolvedFraming === 'unframed' || styleDeltaFor(sp, sz, {
          variantId: p.resolvedVariantId || sp.currentVariantId,
          framing: p.resolvedFraming,
          border: p.border || 'no-border',
        }) != null);
        if (!purchasable.length) return '';
        const fm = all.find(f => f.handle === framingHandle) || {};
        const head = fm.label || fm.name || fm.short_name
          || (framingHandle === 'unframed' ? T('frame.format_unframed', 'Print only') : (group[0]?.framing || framingHandle));
        return `
          <div class="surgical-fd-group">
            <div class="surgical-fd-group-head">${head}</div>
            <div class="surgical-fd-grid">
              ${purchasable.map(p => {
                const isUnframed = p.resolvedFraming === 'unframed';
                const isOn = p.resolvedFraming === curFraming && (p.frameColorHandle || null) === (curColor || null);
                // Delta vs the unframed print (see REFERENCE PRICING) — so a
                // canvas preset carries its base-price difference, and "The
                // Bare Canvas" (wrapped, unframed) shows a delta too.
                const delta = styleDeltaFor(sp, sz, {
                  variantId: p.resolvedVariantId || sp.currentVariantId,
                  framing: p.resolvedFraming,
                  border: p.border || 'no-border',
                });
                const priceLabel = delta
                  ? `+${getCurrencyFmt()(delta)}`
                  : (isUnframed ? T('frame.included', 'Included') : '');
                const cardProductType = p.resolvedVariantId
                  ? (sp.variantConfigurators?.variants?.[p.resolvedVariantId]?.artProduct === 'canvas-print' ? 'canvas' : 'art_print')
                  : productType;
                // Instant thumb from the SSR preset mock; fall back to the lazy
                // loading placeholder (filled by _mountDrawerMocks) when absent.
                const mock = p.mocks?.[orientation];
                const thumb = (mock && mock.frameImage)
                  ? cssCornerCropHTML(mock, artUrl, isUnframed, 1000)
                  : this._buildCornerMockHTML(p.resolvedFraming, { handle: p.frameColorHandle }, cardProductType, p.border || 'no-border');
                return `
                  <button type="button" class="surgical-fd-card ${isOn ? 'on' : ''}"
                          data-framing="${p.resolvedFraming}" data-frame-color="${p.frameColorHandle || ''}"
                          data-variant-id="${p.resolvedVariantId || ''}"
                          data-border="${p.border || 'no-border'}">
                    <div class="surgical-fd-thumb">${thumb}</div>
                    <div class="surgical-fd-info">
                      <div class="name">${p.name}</div>
                      <div class="sub">${isUnframed ? T('frame.unframed_sub', 'Frame it yourself') : (p.description || '')}</div>
                      ${priceLabel ? `<div class="price">${priceLabel}</div>` : ''}
                    </div>
                  </button>`;
              }).join('')}
            </div>
          </div>`;
      }).join('');

      const data = this._computeBuildData();

      this.innerHTML = `
        <dialog class="surgical-fd">
          <div class="surgical-fd-inner">
            <div class="surgical-fd-head">
              <span class="surgical-fd-pull"></span>
              <button type="button" class="surgical-fd-tab ${this._tab === 'all' ? 'on' : ''}" data-tab="all">All Frames</button>
              <button type="button" class="surgical-fd-tab ${this._tab === 'custom' ? 'on' : ''}" data-tab="custom">Build your own</button>
              <button type="button" class="surgical-fd-close" data-close aria-label="Close">×</button>
            </div>

            <div class="surgical-fd-body" data-tabbody="all" ${this._tab === 'all' ? '' : 'hidden'}>
              ${groupedHTML}
              <div class="surgical-fd-foot">
                <span>Want more control?</span>
                <button type="button" class="surgical-fd-link" data-tab="custom">Build your own ›</button>
              </div>
            </div>

            <div class="surgical-fd-body" data-tabbody="custom" ${this._tab === 'custom' ? '' : 'hidden'}>
              ${this._buildCustomBodyHTML(data)}
            </div>

            <div class="surgical-fd-buildfoot" ${this._tab !== 'custom' ? 'hidden' : ''}>
              ${this._buildFootHTML(data)}
            </div>
          </div>
        </dialog>
      `;
      this._wire();
      // Fill thumbnail and BYO preview placeholders asynchronously (awaits mocks).
      this._mountDrawerMocks();
      this._mountBYOMock();
    }

    // Patch only the BYO body + footer in-place — no dialog re-create, no flash
    _refreshBuildUI() {
      const data = this._computeBuildData();
      const body = this.querySelector('[data-tabbody="custom"]');
      if (!body) { this._render(); return; }
      body.innerHTML = this._buildCustomBodyHTML(data);
      const foot = this.querySelector('.surgical-fd-buildfoot');
      if (foot) foot.innerHTML = this._buildFootHTML(data);
      this._wireBuildSteppers();
      this._mountBYOMock();
    }

    /** Fill drawer thumbnail placeholders with CSS corner-crop mocks (async).
     *  Called after _render() so the dialog is already in the DOM.
     *
     *  No _mountSeq guard needed here (unlike the picker's _mountMocks, which
     *  captures its card elements BEFORE awaiting): the placeholder query runs
     *  AFTER the await against the live DOM, and the mock is resolved from the
     *  placeholder's own data attributes — so a re-render during the await
     *  just means this call fills the new placeholders with the new data. */
    async _mountDrawerMocks() {
      const gallery = document.querySelector('media-gallery');
      if (gallery?.ensureAllMocksLoaded) await gallery.ensureAllMocksLoaded();
      else await gallery?.ensureFullMocksLoaded?.();
      const artUrl = resolveArtUrl();
      const sp = this.sp;

      this.querySelectorAll('.surgical-fd-thumb .surgical-cc--loading').forEach(placeholder => {
        const framingHandle = placeholder.dataset.framing || '';
        const colorHandle = placeholder.dataset.colorHandle || '';
        const border = placeholder.dataset.border || 'no-border';
        const isUnframed = placeholder.dataset.isUnframed === 'true';

        let mock = null;
        if (gallery) {
          // Find the framing entry to look up the variant ID. Unframed/canvas
          // have real mocks too (print/canvas at framed dimensions).
          const all = this._allFramings();
          const framingEntry = all.find(f => f.handle === framingHandle);
          const variantId = framingEntry?._variantId || sp.currentVariantId;
          mock = gallery.getProductMockForPreset?.({
            resolvedVariantId: variantId,
            frameColorHandle: isUnframed ? null : colorHandle,
            border,
            resolvedFraming: isUnframed ? 'unframed' : framingHandle,
          }) || null;
        }

        const isWrap = isUnframed;
        const html = cssCornerCropHTML(mock, artUrl, isWrap, 1000);
        placeholder.parentElement.innerHTML = html;
      });
    }

    /** Fill the BYO preview placeholder with a full CSS mock (async).
     *  Called after _refreshBuildUI() (or _render() for initial render). */
    async _mountBYOMock() {
      const gallery = document.querySelector('media-gallery');
      if (gallery?.ensureAllMocksLoaded) await gallery.ensureAllMocksLoaded();
      else await gallery?.ensureFullMocksLoaded?.();
      const artUrl = resolveArtUrl();
      const sp = this.sp;

      const placeholder = this.querySelector('.surgical-fd-preview .surgical-cc--loading');
      if (!placeholder) return;

      const framingHandle = placeholder.dataset.framing || '';
      const colorHandle = placeholder.dataset.colorHandle || '';
      const border = placeholder.dataset.border || 'no-border';
      const isUnframed = placeholder.dataset.isUnframed === 'true';

      let mock = null;
      if (gallery) {
        const all = this._allFramings();
        const framingEntry = all.find(f => f.handle === framingHandle);
        const variantId = framingEntry?._variantId || sp.currentVariantId;
        mock = gallery.getProductMockForPreset?.({
          resolvedVariantId: variantId,
          frameColorHandle: isUnframed ? null : colorHandle,
          border,
          resolvedFraming: isUnframed ? 'unframed' : framingHandle,
        }) || null;
      }

      const html = cssFullMockHTML(mock, artUrl);
      placeholder.outerHTML = html;
    }

    _refreshSelections() {
      const cur = this._snapshotBuild();
      this._build = cur;
      this.querySelectorAll('.surgical-fd-card').forEach(c => {
        // Border must participate (matching the initial render's isOn check)
        // — without it a mount preset stays highlighted after the confirmed
        // state drops to no-border, and vice versa.
        const isOn = c.dataset.framing === cur.framing
          && c.dataset.frameColor === cur.frame_color
          && (c.dataset.border || 'no-border') === (cur.border || 'no-border');
        c.classList.toggle('on', isOn);
      });
    }

    _wire() {
      const dlg = this.querySelector('dialog');
      this.querySelector('[data-close]')?.addEventListener('click', () => this.close());
      dlg?.addEventListener('click', (e) => { if (e.target === dlg) this.close(); });
      dlg?.addEventListener('cancel', () => { document.body.style.overflow = ''; });

      // Tab switch — swap body visibility + footer, no dialog re-create
      this.querySelectorAll('[data-tab]').forEach(btn => {
        btn.addEventListener('click', () => {
          const tab = btn.dataset.tab;
          this._tab = tab;
          this.querySelectorAll('.surgical-fd-tab').forEach(t => t.classList.toggle('on', t.dataset.tab === tab));
          this.querySelectorAll('[data-tabbody]').forEach(b => { b.hidden = b.dataset.tabbody !== tab; });
          const foot = this.querySelector('.surgical-fd-buildfoot');
          if (foot) foot.hidden = tab !== 'custom';
        });
      });

      // All-frames card click — apply immediately + close
      this.querySelectorAll('.surgical-fd-card').forEach(card => {
        card.addEventListener('click', () => {
          this._setFraming(card.dataset.framing, card.dataset.frameColor, card.dataset.variantId, card.dataset.border);
          emitPresetSelected({
            presetId: 'all-frames',
            framing: card.dataset.framing || null,
            frameColor: card.dataset.frameColor || null,
            border: card.dataset.border || null,
            source: 'sheet',
          });
          this.close();
        });
      });

      this._wireBuildSteppers();
    }

    // Wire BYO steppers + apply — safe to call after _refreshBuildUI replaces body
    _wireBuildSteppers() {
      this.querySelectorAll('.surgical-fd-opt').forEach(btn => {
        btn.addEventListener('click', () => {
          const { step, value } = btn.dataset;
          if (step === 'format') {
            this._build.productType = value;
          } else if (step === 'framing') {
            this._build.framing = value;
            const fr = this._allFramings().find(f => f.handle === value);
            if (fr && (fr.colors || []).length) {
              const stillValid = fr.colors.find(c => c.handle === this._build.frame_color);
              if (!stillValid) this._build.frame_color = fr.colors[0].handle;
            } else {
              this._build.frame_color = '';
            }
            if (value === 'unframed' && this._build.border === 'mount') {
              this._build.border = 'no-border';
            }
          } else if (step === 'color') {
            this._build.frame_color = value;
          } else if (step === 'border') {
            this._build.border = value;
          }
          this._refreshBuildUI();
        });
      });

      this.querySelector('[data-apply]')?.addEventListener('click', () => {
        const sp = this.sp;

        // ecd8: Apply must also switch the underlying variant when the
        // user picks Canvas vs Art print in BYO, otherwise the framing
        // option commits onto the wrong variant and ATC resolves to an
        // unavailable combo. Look up the variant matching the new
        // productType and route through the same handoff path as the
        // "All Frames" card click.
        const targetArtProduct = this._build.productType === 'canvas' ? 'canvas-print' : 'art-print';
        const targetVariantEntry = Object.entries(sp.variantConfigurators?.variants || {})
          .find(([, vd]) => vd.artProduct === targetArtProduct);
        const targetVariantId = targetVariantEntry?.[0];
        const switching = targetVariantId && targetVariantId !== sp.currentVariantId;
        if (switching) {
          sp.currentVariantId = targetVariantId;
          // Sync the URL to the build selection BEFORE loading the new
          // variant's configurator. _loadVariantConfigurator → _setInitialState
          // re-reads the URL; if it still held the previous variant's framing
          // (invalid for the new variant) it would fall back to the variant
          // default ('unframed') and emit a stale selection-changed — which is
          // what made a framed canvas show as "Wrapped Canvas".
          try {
            const url = new URL(window.location.href);
            url.searchParams.set('variant', targetVariantId);
            url.searchParams.set('framing', this._build.framing);
            if (this._build.framing !== 'unframed' && this._build.frame_color) {
              url.searchParams.set('frame-color', this._build.frame_color);
            } else {
              url.searchParams.delete('frame-color');
            }
            url.searchParams.set('border', this._build.border || 'no-border');
            history.replaceState({}, '', url.toString());
          } catch {}
          sp._loadVariantConfigurator(targetVariantId);
        }

        // Commit the build selection BEFORE broadcasting variant-changed —
        // listeners (e.g. the frame picker) re-render on that event and must
        // see the final framing/colour/border, not the variant defaults.
        sp.state.confirmed.framing_option = this._build.framing;
        if (this._build.framing !== 'unframed') {
          sp.state.confirmed.frame_color = this._build.frame_color;
        }
        sp.state.confirmed.border_option = this._build.border;

        if (switching) {
          sp.container?.dispatchEvent(new CustomEvent('sku-picker:variant-changed', {
            bubbles: true,
            detail: { variantId: targetVariantId, productType: this._build.productType },
          }));
        }

        sp._updateConfirmedSelection?.(true);
        emitPresetSelected({
          presetId: 'custom',
          framing: this._build.framing,
          frameColor: this._build.framing !== 'unframed' ? this._build.frame_color : null,
          border: this._build.border,
          source: 'sheet',
        });
        this.close();
      });
    }

    _setFraming(framingHandle, colorHandle, variantId, borderHandle) {
      const sp = this.sp;
      if (variantId && variantId !== sp.currentVariantId) {
        sp.currentVariantId = variantId;
        sp._loadVariantConfigurator(variantId);
        const artProduct = sp.variantConfigurators?.variants?.[variantId]?.artProduct;
        const productType = artProduct === 'canvas-print' ? 'canvas' : 'art_print';
        sp.container?.dispatchEvent(new CustomEvent('sku-picker:variant-changed', {
          bubbles: true,
          detail: { variantId, productType },
        }));
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variantId);
          history.replaceState({}, '', url.toString());
        } catch {}
      }
      sp.state.confirmed.framing_option = framingHandle;
      if (framingHandle !== 'unframed' && colorHandle) {
        sp.state.confirmed.frame_color = colorHandle;
      }
      // Apply the frame's border (e.g. a mount) — the All Frames card carries
      // the matched preset's border. Without this the border keeps its prior
      // value and a mount preset silently degrades to no-border.
      if (borderHandle) {
        sp.state.confirmed.border_option = borderHandle;
      }
      if (framingHandle === 'unframed' && sp.state.confirmed.border_option === 'mount') {
        sp.state.confirmed.border_option = 'no-border';
      }
      sp._updateConfirmedSelection?.(true);
    }
  }
  customElements.define('pdp-surgical-frame-drawer', SurgicalFrameDrawer);

  // Bind "View all N frames" trigger → All Frames tab
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-pdp-frame-all]');
    if (!t) return;
    const drawer = document.querySelector('pdp-surgical-frame-drawer');
    if (drawer && typeof drawer.open === 'function') drawer.open();
  });

  // Bind "About this style" trigger → style-picker about dialog
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-pdp-about-style]');
    if (!t) return;
    const sp = document.querySelector('style-picker');
    if (sp && typeof sp.openAboutDialog === 'function') sp.openAboutDialog();
  });

  // ============================================================
  // ADVISOR opener — delegated click handler (catches runtime-injected
  // buttons like the size-picker advisor button that appear after
  // pdp-surgical.js's per-element binding pass). Dispatches the same
  // whisper:open-chat event the bridge in assets/whisper-block.js
  // listens for; that opens the gallery-wall DiscoverDrawer with the
  // PDP product set as focusedPiece.
  // ============================================================
  function getFocusedPiece() {
    const handle = location.pathname.match(/\/products\/([^/?#]+)/)?.[1] || null;
    if (!handle) return null;
    const titleSelectors = [
      '.product-title-h1',
      '.rmf-title',
      '.product-details h1',
      'h1[data-product-title]',
      'main h1',
    ];
    let titleEl = null;
    for (const sel of titleSelectors) {
      titleEl = document.querySelector(sel);
      if (titleEl) break;
    }
    const artistEl = document.querySelector('[data-product-artist], .product-artist, .artist-name');
    const imgEl = document.querySelector(
      'product-media img, .product-image img, [data-product-featured-image], .rmf-hero img, .rmf-preview img'
    );
    return {
      id: handle,
      handle,
      title: titleEl?.textContent.trim() || null,
      artist: artistEl?.textContent.trim() || null,
      imageUrl: imgEl?.currentSrc || imgEl?.src || null,
      type: 'catalog',
      openedFrom: 'pdp',
    };
  }

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-advisor-open]');
    if (!trigger) return;
    // pdp-surgical.js's bindAdvisorOpeners() marks elements it has bound;
    // skip those here so we don't double-fire on the same click.
    if (trigger.dataset.pdpAdvisorBound === '1') return;
    e.preventDefault();

    const focusedPiece = getFocusedPiece();
    document.dispatchEvent(new CustomEvent('whisper:open-chat', {
      detail: {
        intent: trigger.dataset.advisorIntent || 'general',
        userAction: trigger.dataset.advisorPrompt || null,
        productHandle: focusedPiece?.handle || null,
        focusedPiece,
        whisper: trigger.dataset.advisorGreeting ? { text: trigger.dataset.advisorGreeting } : undefined,
      },
    }));
  });

  // ============================================================
  // STICKY CTA + FLOATING CHIP (idempotent boot)
  // ============================================================
  function initStickyCta() {
    const sticky = document.querySelector('[data-pdp-sticky]');
    const buyBlock = document.querySelector('.buy-buttons-block, .product-form-buttons');
    if (!sticky || !buyBlock) return;
    if (sticky.dataset.pdpInit === '1') return;
    sticky.dataset.pdpInit = '1';
    new IntersectionObserver(([e]) => {
      const visible = !e.isIntersecting && e.boundingClientRect.top < 0;
      sticky.classList.toggle('visible', visible);
      sticky.setAttribute('aria-hidden', visible ? 'false' : 'true');
    }, { threshold: 0 }).observe(buyBlock);

    sticky.querySelector('[data-pdp-sticky-atc]')?.addEventListener('click', () => {
      const realBtn = document.querySelector('.add-to-cart-button, button[name="add"]');
      realBtn?.click();
    });

    const update = () => {
      // Title
      const titleEl = document.querySelector('.product-title-h1, .product-details h1');
      const stickyTitle = sticky.querySelector('.pdp-sticky-cta__title');
      if (titleEl && stickyTitle) stickyTitle.textContent = titleEl.textContent.trim();

      // Sub line. Configurator v2 owns this string: it is the same summary the
      // collapsed pinned-mock strip shows, produced by `configSummary()` from
      // the configurator's own state.
      //
      // What this replaced, and why: the old chain read
      // `confirmed.framing_option` through FRAMING_DISPLAY_NAMES, so a shopper
      // who picked the OAK chip was told "Classic" — the framing FAMILY, a word
      // v2 never puts on screen, and identical across every colour in that
      // family. It also blanked the frame part entirely for `unframed` (so a
      // print-only line and a framed line rendered the same), never mentioned
      // the format or the added upgrades, and looked up a size bucket from
      // `pdp-size-picker`, which v2 replaces wholesale — always empty.
      const v2 = document.querySelector('pdp-configurator-v2');

      let config;
      if (v2) {
        // v2 element present → v2 OWNS this line, full stop. Branching on
        // `summary.text` truthiness instead of element presence let the
        // legacy branch's family-name vocabulary ("Classic") leak onto a v2
        // page: `configSummary()` answers `{ text: '' }` not just when there
        // is no configurator, but also in the window before its async
        // `_ready()` resolves — a real (if narrow) case, since this first
        // `update()` runs synchronously on load, before `window.skuPickerInstance`.
        // An empty sub-line for a few hundred ms is correct; a wrong one is
        // not. The MutationObserver below repaints once state lands.
        config = v2.configSummary?.()?.text || '';
      } else {
        // Legacy PDP (configurator v2 flag off): the bucket picker is real
        // there, and so is the family-name vocabulary it shipped with.
        const sp = window.skuPickerInstance;
        const fr = sp?.state?.confirmed?.framing_option || '';
        const frDisplay = (fr && fr !== 'unframed') ? (FRAMING_DISPLAY_NAMES[fr] || '') : '';

        // Its `.dim` carries the dims in the ACTIVE unit — falling through to
        // the raw handle showed inches while the picker was set to cm.
        const flatOn = document.querySelector('pdp-size-picker .psp-flatcard.on');
        const activeBucket = flatOn?.querySelector('.name')?.textContent.trim()
                          || document.querySelector('pdp-size-picker .psp-bucket.on .name')?.textContent.trim()
                          || '';
        const sizeText = flatOn?.querySelector('.dim')?.textContent.trim()
                      || document.querySelector('pdp-size-picker .psp-subsize.on b')?.textContent.trim()
                      || sp?.state?.confirmed?.size_option
                      || '';

        const sizePart = [activeBucket, sizeText].filter(Boolean).join(' ');
        config = `${sizePart}${frDisplay ? `, ${frDisplay}` : ''}`.trim();
      }

      const configEl = sticky.querySelector('[data-pdp-sticky-config]');
      if (configEl) configEl.textContent = config;

      // Prices — current is always present, compare-at only when on sale.
      // Scope to the v2-price block: it's the canonical configurator-driven
      // price. Other blocks also carry .price-compare-at, so an unscoped
      // query can grab the wrong one.
      const cur = document.querySelector('.product-information--surgical .v2-price .price-current')?.textContent.trim() || '';
      const cmp = document.querySelector('.product-information--surgical .v2-price .price-compare-at')?.textContent.trim() || '';
      const priceEl = sticky.querySelector('[data-pdp-sticky-price]');
      const compareEl = sticky.querySelector('[data-pdp-sticky-price-compare]');
      if (priceEl) priceEl.textContent = cur;
      if (compareEl) {
        if (cmp && cmp !== cur) {
          compareEl.textContent = cmp;
          compareEl.hidden = false;
        } else {
          compareEl.textContent = '';
          compareEl.hidden = true;
        }
      }

      // Mirror the hero's CSS frame mock (frame + artwork) into the sticky
      // swatch so it shows the FRAMED preview, not bare artwork. Clone the hero
      // .mock-image-container — but DEFER until the hero's async mock-swap has
      // settled (mocks loaded + next frame), otherwise we'd freeze the SSR
      // default mock and the sticky would show the wrong/unframed image.
      const swatch = sticky.querySelector('.pdp-sticky-cta__swatch');
      const fallback = sticky.querySelector('[data-pdp-sticky-thumb-fallback]');
      const cloneHeroIntoSticky = () => {
        const heroMock =
          document.querySelector('.product-information--surgical [data-slide-role="frame-mock"] .mock-image-container') ||
          document.querySelector('.mock-image-container');
        if (swatch && heroMock) {
          const clone = heroMock.cloneNode(true);
          clone.className = 'mock-image-container pdp-sticky-cta__mock';
          clone.removeAttribute('data-slide-role');
          clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
          const existing = swatch.querySelector('.pdp-sticky-cta__mock');
          if (existing) existing.replaceWith(clone);
          else swatch.appendChild(clone);
          if (fallback) fallback.style.display = 'none';
        } else if (fallback) {
          // No hero mock — fall back to the artwork image (queried separately so
          // the empty-centre frame overlay, first in DOM order, isn't picked).
          const mainImg =
            document.querySelector('.product-information--surgical [data-slide-role="frame-mock"] .mock-image__placement img') ||
            document.querySelector('.product-information--surgical .product-media-container .product-media__image, .product-information--surgical slideshow-slide[aria-current="true"] img, .product-information--surgical slideshow-slide img');
          const src = mainImg?.currentSrc || mainImg?.src;
          if (src && fallback.src !== src) fallback.src = src;
        }
      };
      const gallery = document.querySelector('media-gallery');
      if (gallery && gallery.ensureFullMocksLoaded) {
        gallery.ensureFullMocksLoaded()
          .then(() => requestAnimationFrame(cloneHeroIntoSticky))
          .catch(() => cloneHeroIntoSticky());
      } else {
        requestAnimationFrame(cloneHeroIntoSticky);
      }
    };
    update();
    document.addEventListener('sku-picker:selection-changed', update);
    document.addEventListener('sku-picker:variant-changed', update);
    // Picker change → re-pull bucket + formatted size from its DOM.
    document.addEventListener('pdp-size-picker:change', update);
    // Unit toggle (in|cm) re-renders the picker without a selection event —
    // defer one tick so the sticky sub line re-reads the re-rendered dims.
    // `[data-pcv2-unit]` as well as `[data-unit]`: v2's toggle uses its own
    // attribute, so this handler silently stopped matching and the sticky kept
    // whichever unit was current when the last SIZE change happened.
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-unit], [data-pcv2-unit]')) setTimeout(update, 80);
    });
    // v2 re-renders its chips ASYNCHRONOUSLY after the selection event, so an
    // update() driven straight off `sku-picker:selection-changed` reads the chip
    // still pressed from the PREVIOUS selection. Measured: after a size change
    // the picker said "75 × 100 cm" while the sticky still said "30 × 40 cm".
    // Observing the configurator fixes the ordering — by the time a mutation
    // fires, the new markup exists — and covers size, frame, format and unit
    // changes with one mechanism. Coalesced to one update per frame because a
    // re-render emits many mutations.
    const v2Root = document.querySelector('pdp-configurator-v2');
    if (v2Root) {
      let queued = false;
      const queueUpdate = () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => { queued = false; update(); });
      };
      new MutationObserver(queueUpdate).observe(v2Root, {
        childList: true,
        subtree: true,
        attributeFilter: ['aria-pressed'],
      });
    }
    // The picker mounts async after the SKU picker boots; if its DOM isn't
    // there yet at first paint, retry a few times so the sub line populates
    // before the user interacts.
    let pickerTries = 0;
    const pickerPoll = setInterval(() => {
      pickerTries += 1;
      // The hybrid configurator marks a .psp-flatcard.on; the legacy picker a
      // .psp-bucket.on. Polling only for the bucket left the sticky sub line on
      // the raw handle ("9-x-12") at landing under the hybrid picker (QA 09/07).
      // v2's pressed size chip counts as "the picker is up" too, or this polls
      // for 10s against a picker that never mounts in that arm and the sub line
      // sits on whatever the first update() found.
      if (document.querySelector(
        'pdp-size-picker .psp-flatcard.on, pdp-size-picker .psp-bucket.on, pdp-configurator-v2 [data-pcv2-size][aria-pressed="true"] .pcv2-size__dim'
      )) {
        update();
        clearInterval(pickerPoll);
      } else if (pickerTries > 40) {
        clearInterval(pickerPoll);
      }
    }, 250);
    // Also update when the gallery slide changes
    const thumbsList = document.querySelector('.product-information--surgical .product-media-thumbnails__list, .product-information--surgical slideshow-component');
    if (thumbsList) new MutationObserver(update).observe(thumbsList, { attributes: true, subtree: true, attributeFilter: ['aria-current'] });

    // Live framed-mock blit — find the page's <gallery-wall-frame-mock>
    // (the main, non-corner-crop instance used for the recommended trio)
    // and register the sticky <canvas> as a thumbnail target so every
    // re-render of the mock paints into the sticky swatch. Polls briefly
    // because the mock element mounts lazily after the configurator boots.
    // The hero is now a CSS mock-image (no <gallery-wall-frame-mock> canvas to
    // blit from), so retire the canvas thumbnail target — the artwork fallback
    // <img> (set in update()) is the sticky swatch.
    const stickyCanvas = sticky.querySelector('canvas[data-pdp-sticky-thumb]');
    if (stickyCanvas) stickyCanvas.style.display = 'none';
  }

  // ============================================================
  // UTILITY BAR — removed per user request 2026-09-22. The global theme
  // header already carries an announcement bar with the same promo/returns
  // copy, so this second black strip duplicated it (and leaked onto any page
  // with a header, e.g. behind the account slideout). Same call made on the
  // readymade-frame PDP (see sections/readymade-frame-pdp.liquid).
  // ============================================================

  // Per-slide pills (Frame / 3D on slide 0, Example wall / See on your wall
  // on slide 1) own their own click handlers via pdp-surgical-room-preview.js.
  // The old global Print/Frame/Wall gallery pill has been removed.
  function initGalleryModeTabs() { /* no-op — per-slide pills */ }

  // ============================================================
  function initFloatingChip() {
    const chip = document.querySelector('[data-pdp-aa-chip]');
    if (!chip || chip.dataset.pdpInit === '1') return;
    chip.dataset.pdpInit = '1';
    // Reparent to <body> so position: fixed escapes any ancestor with
    // transform/contain/will-change that would create a containing block.
    if (chip.parentElement !== document.body) document.body.appendChild(chip);
    if (sessionStorage.getItem('pdp_advisor_chip_dismissed') === '1') return;
    const onScroll = () => chip.classList.toggle('visible', window.scrollY > 280);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    chip.addEventListener('click', (ev) => {
      if (ev.target.closest('[data-pdp-aa-chip-dismiss]')) {
        ev.stopPropagation();
        sessionStorage.setItem('pdp_advisor_chip_dismissed', '1');
        chip.classList.remove('visible');
        chip.style.display = 'none';
        return;
      }
      openAdvisor('chip');
    });
  }

  function initFrameDrawer() {
    const drawer = document.querySelector('[data-pdp-frame-drawer]');
    if (!drawer || drawer.dataset.pdpInit === '1') return;
    drawer.dataset.pdpInit = '1';

    const close = () => {
      if (typeof drawer.close === 'function') drawer.close();
      else drawer.removeAttribute('open');
      document.body.style.overflow = '';
    };
    drawer.querySelector('[data-pdp-fb-close]')?.addEventListener('click', close);
    drawer.addEventListener('click', (e) => { if (e.target === drawer) close(); });
    drawer.addEventListener('cancel', () => { document.body.style.overflow = ''; });

    drawer.querySelectorAll('[data-pdp-fb-tab]').forEach(tab => {
      tab.addEventListener('click', () => {
        const which = tab.dataset.pdpFbTab;
        drawer.querySelectorAll('.tab').forEach(t => t.classList.toggle('on', t.dataset.pdpFbTab === which));
        drawer.querySelectorAll('[data-pdp-fb-tabbody]').forEach(b => {
          b.hidden = b.dataset.pdpFbTabbody !== which;
        });
      });
    });
  }

  // ============================================================
  // TRUST + DELIVERY TELEPORT — move below the ATC button
  // ============================================================
  // ============================================================
  // Teleport gallery actions row + Discover portal under the gallery
  // (snippet emits <template> nodes that this clones into the gallery column)
  // ============================================================
  function teleportGalleryExtras() {
    const media = document.querySelector('.product-information--surgical .product-information__media');
    if (!media) return;

    // Discover portal — MOVE the LIVE <collection-portal> from the right column.
    // Desktop: append inside media container (aligns under gallery image column).
    // Mobile: append after the media container so it scrolls under the sticky gallery.
    if (!document.querySelector('.pdp-v2-discover-wrapper')) {
      const livePortal = document.querySelector('.product-information--surgical .product-details collection-portal, .product-information--surgical .product-information__details collection-portal');
      const wrap = document.createElement('div');
      wrap.className = 'pdp-v2-discover-wrapper';
      if (livePortal) {
        wrap.appendChild(livePortal);
      } else {
        const railTpl = document.getElementById('pdp-surgical-more-by-artist-tpl');
        if (railTpl) wrap.appendChild(railTpl.content.cloneNode(true));
      }
      // Portal lives INSIDE the gallery media container on every viewport.
      // On mobile the slideshow (media-gallery) is the sticky element and
      // .product-information__media is its bounding box — so the gallery pins
      // only while the portal scrolls past, then releases for the configurator.
      // (The gallery itself is never DOM-moved — only this portal node is.)
      media.appendChild(wrap);
    }

  }

  function teleportTrustBlock() {
    const trust = document.querySelector('[data-pdp-trust]');
    if (!trust || trust.dataset.pdpTeleported === '1') return;
    const buy = document.querySelector('.product-information--surgical .buy-buttons-block');
    if (!buy) return;
    // The buy-buttons sit inside a horizon group-block flex row. Walk up to
    // the group-block ancestor itself, then drop the trust block AFTER that
    // group so it claims a new row in the parent column flex.
    const groupBlock = buy.closest('.group-block') || buy.parentElement;
    if (!groupBlock) return;
    groupBlock.insertAdjacentElement('afterend', trust);
    trust.hidden = false;
    trust.dataset.pdpTeleported = '1';
  }

  // ============================================================
  // Hide horizon's legacy trust/returns/customs row block, which duplicates
  // the redesigned trust block we just placed under the ATC.
  // ============================================================
  function hideLegacyTrustGroup() {
    const details = document.querySelector('.product-information--surgical .product-details');
    if (!details) return;
    const phrases = [
      /Made-to-order in .* – no customs fees/i,
      /Free 14-day returns\s*–\s*no questions asked/i,
      /We offset carbon emissions on every order/i,
      /5-star customer support on live chat/i
    ];
    const all = Array.from(details.querySelectorAll('p, span, .rte, [class*="text-block"]'));
    all.forEach(el => {
      if (!el || el.children.length > 0) return;
      const txt = el.textContent || '';
      if (!phrases.some(p => p.test(txt))) return;
      // Walk up to the IMMEDIATE .group-block parent (the row container).
      // Each phrase lives in its own row block, so hiding the row block
      // hides just that row without collapsing the surrounding column.
      let p = el.parentElement;
      while (p && p !== details) {
        if (p.classList?.contains('group-block')) {
          p.style.display = 'none';
          break;
        }
        p = p.parentElement;
      }
    });

    // Also hide the legacy stars/rating row (4.8 + happy customers link).
    // The price snippet decorates a Stars.svg image + an adjacent text-block
    // with rating text. Find both and hide their group-block wrapper.
    const starsImg = details.querySelector('img[src*="Stars"], img[src*="stars"]');
    if (starsImg) {
      let p = starsImg.parentElement;
      while (p && p !== details) {
        if (p.classList?.contains('group-block')) { p.style.display = 'none'; break; }
        p = p.parentElement;
      }
    }
  }

  // ATC price chip removed — designed layout shows price in standalone block above ATC.

  // Force the price-countdown-badge text to the "Save NN%" form (per design),
  // stripping the legacy "· countdown" suffix. The wording comes from the
  // i18n island (pdp_surgical.price.save_badge) so every locale keeps its
  // own phrasing. Observes the price root so we catch badge replacements
  // (countdown timer rerenders the badge node).
  // While a Q4 stage has an end (snippets/campaign-offer-line.liquid renders
  // [data-pdp-offer-end]), the pill also carries its countdown:
  // "Save 40% · 2d 04h 12m 33s" (<campaign-countdown>, assets/campaign-countdown.js).
  function rewriteSaveBadge() {
    const template = T('price.save_badge', 'Save __P__%');
    const offerEnd = document.querySelector('[data-pdp-offer-end]')?.dataset.pdpOfferEnd || '';
    // The % shown is the market's discount setting (custom.art_sitewide_discount),
    // as in the announcement bar and cart, not the saving worked out from this
    // size's prices.
    const offerPct = document.querySelector('[data-pdp-offer-pct]')?.dataset.pdpOfferPct || '';
    const fix = () => {
      document.querySelectorAll('.product-information--surgical .price-countdown-badge').forEach(badge => {
        const label = badge.querySelector('[data-save-label]');
        const txt = ((label || badge).textContent || '').trim();
        const m = txt.match(/(\d{1,3})\s*%/);
        if (!m) return;
        const wanted = template.replace('__P__', offerPct || m[1]);
        if (!offerEnd) {
          if (txt === wanted && !label) return;
          badge.textContent = wanted;
          return;
        }
        if (label && txt === wanted && badge.querySelector('campaign-countdown')) return;
        badge.textContent = '';
        const span = document.createElement('span');
        span.dataset.saveLabel = '';
        span.textContent = wanted;
        const timer = document.createElement('campaign-countdown');
        timer.dataset.end = offerEnd;
        timer.className = 'price-countdown-badge__timer';
        timer.innerHTML = '· <span data-countdown-time></span>';
        badge.append(span, timer);
      });
    };
    fix();
    // Watch the price root for badge replacements; rerun fix when DOM changes.
    const root = document.querySelector('.product-information--surgical .final-product-price, .product-information--surgical .v2-price') || document.querySelector('.product-information--surgical');
    if (root && !root.__pdpBadgeMo) {
      root.__pdpBadgeMo = new MutationObserver(fix);
      root.__pdpBadgeMo.observe(root, { childList: true, subtree: true, characterData: true });
      bootSignal?.addEventListener('abort', () => {
        root.__pdpBadgeMo?.disconnect();
        delete root.__pdpBadgeMo;
      }, { once: true });
    }
    // Also retry for a few seconds in case the badge mounts late.
    let tries = 0;
    const iv = setInterval(() => {
      tries += 1;
      fix();
      if (tries > 25) clearInterval(iv);
    }, 200);
    bootSignal?.addEventListener('abort', () => clearInterval(iv), { once: true });
  }

  // Price now renders as a single block (v2-price) directly above the buy
  // buttons via template order — no duplicate-price hiding or ATC price-chip
  // injection needed. (Removed: hideDuplicatePriceBlocks, injectAtcPriceChip.)

  // Inject a heart (wishlist) button to the LEFT of the ATC button,
  // matching the design's `.cta-row` (grid 56px / 1fr / heart + cta).
  // Also inject the "Arrives Sat 2 – Mon 4 May · …" help line under CTA.
  function injectHeartAndArrivesLine() {
    const buy = document.querySelector('.product-information--surgical .buy-buttons-block');
    if (!buy) return;
    const form = buy.querySelector('product-form-component, form');
    if (!form) return;

    // Heart button — drives the shared WishlistState so any other wishlist
    // surface on the page (meta-row saved count, header counter, etc.) stays
    // in sync, and re-renders itself in response to changes from elsewhere.
    const productHandle = location.pathname.match(/\/products\/([^/?#]+)/)?.[1] || null;
    let heart = buy.querySelector('.pdp-heart-btn');
    if (!heart) {
      const productId = window.ShopifyAnalytics?.meta?.product?.id
        || document.querySelector('product-form-component[data-product-id]')?.dataset.productId
        || null;
      heart = document.createElement('button');
      heart.type = 'button';
      heart.className = 'pdp-heart-btn';
      heart.setAttribute('aria-label', T('a11y.save_wishlist', 'Save to wishlist'));
      heart.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78Z"/></svg>`;

      heart.addEventListener('click', (ev) => {
        ev.preventDefault();
        if (window.WishlistState && productHandle) {
          window.WishlistState.toggle(productHandle, productId);
          return;
        }
        // Fallback if the shared state isn't loaded yet
        const wl = document.querySelector(`wishlist-button[data-product-handle="${productHandle}"], .wishlist-button, [data-wishlist-toggle], button[name="wishlist"]`);
        if (wl) wl.click();
        heart.classList.toggle('on');
      });

      buy.classList.add('pdp-buy--with-heart');
      buy.insertBefore(heart, form);
    }

    // ATC act parity: mark the CTA row + mirror the live configurator price
    // into the Add-to-cart button ("Add to cart · £69.90"). Flag-gated on the
    // presence of the teleported .pdp-atc-act block. Price source is the
    // canonical configurator-driven .v2-price .price-current (same as sticky).
    if (document.querySelector('.pdp-atc-act')) {
      buy.classList.add('pdp-cta--act');
      const atcBtn = buy.querySelector('.add-to-cart-button');
      // Observe the persistent price CONTAINER, not .price-current itself:
      // the sku picker rewrites the container's innerHTML on every selection
      // change, so a .price-current reference goes dead after the first
      // re-render and the button price never updates again (QA thread).
      const priceWrap = document.querySelector('.product-information--surgical .v2-price')
        || document.querySelector('.product-information--surgical .final-product-price');
      if (atcBtn && priceWrap && !atcBtn.querySelector('.pdp-atc-price')) {
        const content = atcBtn.querySelector('.add-to-cart-text__content') || atcBtn.querySelector('.add-to-cart-text');
        const priceSpan = document.createElement('span');
        priceSpan.className = 'pdp-atc-price';
        content.insertAdjacentElement('afterend', priceSpan);
        const syncAtcPrice = () => {
          const p = priceWrap.querySelector('.price-current')?.textContent.trim() || '';
          priceSpan.textContent = p ? '· ' + p : '';
        };
        syncAtcPrice();
        new MutationObserver(syncAtcPrice).observe(priceWrap, { childList: true, characterData: true, subtree: true });
      }
    }

    // (Re)subscribe to wishlist state every boot cycle, OUTSIDE the creation
    // guard: shopify:section:load fires for ANY section's reload (e.g. the
    // merchant saves the header), which aborts the previous cycle's listener
    // — but the heart element survives when the PDP DOM wasn't replaced, so
    // a subscription tied to the creation guard would never come back.
    const syncFromState = () => {
      if (!productHandle || !window.WishlistState) return;
      heart.classList.toggle('on', window.WishlistState.isLiked(productHandle));
    };
    document.addEventListener('wishlist:changed', syncFromState, { signal: bootSignal });
    // Reflect current state once the shared store is hydrated.
    if (window.WishlistState?.storageLoaded) syncFromState();
    else setTimeout(syncFromState, 0);

    // Arrives help line — positioned in the buy block column.
    // Reads the date range from the badges-mode delivery-estimates render
    // (.delivery-estimates__date-range) which is the actual data source on
    // this template. Falls back to "Made to order, ships fast" if absent.
    //
    // f86c + a23f: previously also rendered "Made in UK" and
    // "free delivery over X" here. Both duplicated the trust strip (.pdp-
    // delivery + .pdp-trust-cell) immediately below, so we collapsed this
    // CTA-adjacent reassurance to just the arrival date.
    if (!document.querySelector('.pdp-atc-act') && !buy.querySelector('.pdp-cta-help')) {
      const help = document.createElement('div');
      help.className = 'pdp-cta-help';
      const dateRange = document.querySelector('.product-information--surgical .delivery-estimates__date-range')?.textContent.trim();
      const arrivesPrefix = T('delivery.arrives_prefix', 'Arrives');
      const fallback = T('delivery.fallback_no_date', 'Made to order, ships fast');
      const arrivalCopy = dateRange ? `<b>${arrivesPrefix} ${dateRange}</b>` : `<b>${fallback}</b>`;
      help.innerHTML = arrivalCopy;
      buy.appendChild(help);
    }

    // Rewrite the first .pdp-delivery <li> to a clean text row matching
    // the design ("Arrives Sat 2 – Mon 4 May · free shipping over £59")
    // instead of leaking the badges-mode snippet's flex pill structure.
    // Design QA 09/07 (Vince): no "Order now," lead — must fit one line —
    // and the site says "shipping", not "delivery".
    const firstDeliv = document.querySelector('.product-information--surgical .pdp-delivery > li:first-child .txt');
    if (firstDeliv && !firstDeliv.dataset.pdpRewritten) {
      const dateRange = document.querySelector('.product-information--surgical .delivery-estimates__date-range')?.textContent.trim();
      // Arm-split copy: treatment (ATC act present) says "Arrives … · free
      // shipping over X" (Vince 09/07); control keeps prod's "Order now,
      // arrives … · free delivery over X".
      const isAct = !!document.querySelector('.pdp-atc-act');
      const lead = isAct ? T('delivery.arrives_prefix', 'Arrives') : T('delivery.lead', 'Order now, arrives');
      const fallback = T('delivery.fallback_no_date', 'Made to order, ships fast');
      const freeOverThreshold = isAct
        ? T('delivery.free_shipping_over_tail', '') || T('delivery.free_over_threshold', '')
        : T('delivery.free_over_threshold', '');
      const tail = freeOverThreshold ? ` · ${freeOverThreshold}` : '';
      if (dateRange) {
        firstDeliv.innerHTML = `${lead} <b>${dateRange}</b>${tail}`;
        firstDeliv.dataset.pdpRewritten = '1';
      } else {
        firstDeliv.innerHTML = `<b>${fallback}</b>${tail}`;
        firstDeliv.dataset.pdpRewritten = '1';
      }
    }
  }

  // ============================================================
  // GALLERY PIPS — slide indicator dots below the gallery on mobile
  // ============================================================
  function initGalleryPips() {
    if (window.innerWidth >= 750) return;
    const mediaGallery = document.querySelector('media-gallery[data-new-pdp]') || document.querySelector('.product-information--surgical media-gallery');
    if (!mediaGallery) return;

    const slides = mediaGallery.querySelectorAll('slideshow-slide');
    let pips = mediaGallery.querySelector('.pdp-gallery-pips');

    if (!pips) {
      const count = slides.length;
      if (count < 2) return;

      pips = document.createElement('div');
      pips.className = 'pdp-gallery-pips';
      pips.setAttribute('aria-hidden', 'true');

      for (let i = 0; i < count; i++) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'pdp-gallery-pip' + (i === 0 ? ' pdp-gallery-pip--active' : '');
        btn.dataset.index = i;
        pips.appendChild(btn);
      }

      mediaGallery.appendChild(pips);

      // Element-scoped — lives and dies with the pips node, no signal needed.
      pips.addEventListener('click', (e) => {
        const btn = e.target.closest('.pdp-gallery-pip[data-index]');
        if (!btn) return;
        const idx = parseInt(btn.dataset.index, 10);
        const slideshow = mediaGallery.querySelector('slideshow-component');
        if (slideshow?.select) slideshow.select(idx);
        else {
          const targetSlide = slides[idx];
          if (targetSlide) targetSlide.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        }
      });
    }

    // (Re)create the observer every boot cycle, OUTSIDE the creation guard:
    // shopify:section:load fires for ANY section's reload, which aborts the
    // previous cycle's observer — but the pips element survives when the PDP
    // DOM wasn't replaced, so an observer tied to creation would never
    // come back and the dots would stop tracking the active slide.
    const pipsEl = pips;
    const mo = new MutationObserver(() => {
      const active = parseInt(mediaGallery.getAttribute('data-active-slide') || '0', 10);
      pipsEl.querySelectorAll('.pdp-gallery-pip').forEach((pip, i) => {
        pip.classList.toggle('pdp-gallery-pip--active', i === active);
      });
    });
    mo.observe(mediaGallery, { attributes: true, attributeFilter: ['data-active-slide'] });
    bootSignal?.addEventListener('abort', () => mo.disconnect(), { once: true });
  }

  // Make the duplicated legacy unit toggle inert for keyboard + AT users.
  // The surgical PDP overlays its own picker UI on top of the legacy
  // .size-bucket-slider, but the old in/cm toggle still sits in the DOM
  // and trips up automated tools / screen readers.
  //
  // IMPORTANT: do NOT touch .style-picker / .style-picker__card here —
  // style-picker.js reads those buttons to detect the active preset on
  // variant switch, and aria-hidden makes the cards look unavailable, so
  // it falls back to unframed (regression: changing size resets the frame
  // to Print Only).
  function neutraliseLegacyPickers() {
    const legacySelectors = [
      '.size-bucket-slider__unit-toggle',
      '.size-bucket-slider__unit-btn',
    ];
    legacySelectors.forEach((sel) => {
      document.querySelectorAll(sel).forEach((el) => {
        if (el.dataset.surgicalNeutralised === '1') return;
        if (el.offsetParent === null && !el.matches(':focus-within')) {
          el.setAttribute('aria-hidden', 'true');
          if (el.tagName === 'BUTTON') el.setAttribute('tabindex', '-1');
          el.querySelectorAll('button').forEach((b) => b.setAttribute('tabindex', '-1'));
          el.dataset.surgicalNeutralised = '1';
        }
      });
    });
  }

  let bootAbort = null;
  let bootSignal = null;

  function boot() {
    window.__pdpSurgicalBootCount = (window.__pdpSurgicalBootCount || 0) + 1;
    // One controller per boot cycle: shopify:section:load re-runs boot()
    // against a replaced DOM. Initialisers register their observers,
    // intervals and document listeners against bootSignal so the previous
    // cycle's are torn down instead of stacking.
    bootAbort?.abort();
    bootAbort = new AbortController();
    bootSignal = bootAbort.signal;
    try { initGalleryModeTabs(); } catch (e) { window.__pdpSurgicalErr = ['gallery-tabs', e.message]; }
    try { neutraliseLegacyPickers(); } catch (e) { window.__pdpSurgicalErr = ['legacy-pickers', e.message]; }
    // Inline advisor card is rendered server-side via v2-advisor-card.liquid (template block)
    try { initStickyCta(); } catch (e) { window.__pdpSurgicalErr = ['sticky', e.message]; }
    try { initFloatingChip(); } catch (e) { window.__pdpSurgicalErr = ['chip', e.message]; }
    try { initFrameDrawer(); } catch (e) { window.__pdpSurgicalErr = ['drawer', e.message]; }
    try { teleportGalleryExtras(); } catch (e) { window.__pdpSurgicalErr = ['gallery-teleport', e.message]; }
    try { teleportTrustBlock(); } catch (e) { window.__pdpSurgicalErr = ['trust-teleport', e.message]; }
    try { hideLegacyTrustGroup(); } catch (e) { window.__pdpSurgicalErr = ['legacy-trust', e.message]; }
    try { rewriteSaveBadge(); } catch (e) { window.__pdpSurgicalErr = ['save-badge', e.message]; }
    try { injectHeartAndArrivesLine(); } catch (e) { window.__pdpSurgicalErr = ['heart-arrives', e.message]; }
    try { initGalleryPips(); } catch (e) { window.__pdpSurgicalErr = ['gallery-pips', e.message]; }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
